import { useEffect, useMemo, useRef, useState, useCallback, memo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { List as VirtualList } from "react-window";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useIsDesktop } from "@/hooks/useMediaQuery";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { MessageEditor } from "@/components/MessageEditor";
import { AllDonorsTab } from "@/components/AllDonorsTab";
import { AddDonorDialog } from "@/components/AddDonorDialog";
import { toast } from "sonner";
import {
  Phone, MessageSquare, Mail, ArrowLeft, Search, LogOut, CheckCircle2,
  Voicemail, DollarSign, XCircle, Clock, ChevronLeft, ChevronRight, Copy as CopyIcon,
  StickyNote, Plus, Settings as SettingsIcon, Eye, EyeOff, KeyRound, Download, MessageCircle,
  ChevronDown, ChevronUp, X as CloseIcon, Pencil, Lock,
} from "lucide-react";

/**
 * Caller Mode — a locked-down single-screen donor call tool.
 * Route: /caller. Users with role="caller" cannot navigate anywhere else
 * (router-level gate in App.js). Admins can also visit for testing.
 *
 * Data comes from the isolated `caller_donors` collection via /api/caller/*.
 * Nothing on this page ever touches a customer campaign, voter file, or
 * finance record.
 */

const FILTERS = [
  { key: "all", label: "All" },
  { key: "not_contacted", label: "Not yet called" },
  { key: "follow_ups_due", label: "Follow-ups due" },
];

const STATUS_LABELS = {
  not_contacted: { label: "Not called yet", cls: "bg-slate-100 text-slate-700" },
  reached: { label: "Reached", cls: "bg-emerald-100 text-emerald-900" },
  left_message: { label: "Left message", cls: "bg-amber-100 text-amber-900" },
  pledged: { label: "Pledged", cls: "bg-green-100 text-green-900" },
  declined: { label: "Declined", cls: "bg-slate-200 text-slate-600 line-through" },
  call_back: { label: "Follow up", cls: "bg-sky-100 text-sky-900" },
};

const fmtMoney = (n) => `$${Math.round(Number(n) || 0).toLocaleString()}`;
const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return iso;
  }
};
const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return iso;
  }
};

/**
 * All-time cumulative pledge/reached tracker in the caller shell header.
 * Reads from /caller/stats (lightweight aggregates only — no log rows) so
 * this poll stays cheap even for callers with thousands of entries.
 *
 * "Reached" here matches the session strip: reached count + pledged count
 * (a pledge counts as a reach). See handleOutcomeLogged.
 */
function AllTimeTracker({ refreshKey }) {
  const [stats, setStats] = useState({ pledged: 0, reached: 0 });
  useEffect(() => {
    let cancelled = false;
    api.get("/caller/stats")
      .then(({ data }) => {
        if (cancelled) return;
        const counts = data.counts || {};
        const reached = (counts.reached || 0) + (counts.pledged || 0);
        setStats({ pledged: Number(data.pledged_total || 0), reached });
      })
      .catch(() => { /* silent — tracker is informational */ });
    return () => { cancelled = true; };
  }, [refreshKey]);
  return (
    <Link
      to="/caller/settings"
      className="hidden sm:inline-flex items-center gap-2.5 bg-flag-navy/5 border border-flag-navy/20 hover:bg-flag-navy/10 hover:border-flag-navy/30 rounded-full px-4 py-1.5 text-sm font-semibold text-flag-navy leading-none whitespace-nowrap transition-colors cursor-pointer"
      data-testid="caller-alltime-tracker"
      title="View your outreach log · Cumulative totals across every list, never resets"
    >
      <span className="text-[11px] uppercase tracking-wider font-bold text-flag-navy/70">All time</span>
      <span className="text-flag-navy/30" aria-hidden>·</span>
      <span className="text-emerald-700 tabular-nums" data-testid="alltime-pledged">${Math.round(stats.pledged).toLocaleString()} pledged</span>
      <span className="text-flag-navy/30" aria-hidden>·</span>
      <span className="tabular-nums" data-testid="alltime-reached">{stats.reached} reached</span>
    </Link>
  );
}

export default function CallerMode() {
  const { user, logout } = useAuth();
  const [listStatus, setListStatus] = useState(null);  // {active, house, reference, categories}
  // Per-caller campaign config — cached ONCE at mount so contribution-button
  // branching (federal vs state_matching) can render synchronously without
  // a per-donor fetch. Federal is the safe default — any load error keeps
  // every render on the federal code path, guaranteeing no regressions on
  // existing accounts if the endpoint is temporarily unavailable.
  const [campaignConfig, setCampaignConfig] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api.get("/caller/campaign-config")
      .then(({ data }) => { if (!cancelled) setCampaignConfig(data); })
      .catch(() => {
        if (!cancelled) setCampaignConfig({
          campaign_type: "federal",
          matchable_ceiling: 250,
          current_covered_election: "primary",
        });
      });
    return () => { cancelled = true; };
  }, []);

  // Categories drive tab rendering. Legacy list_type_legacy on the migrated
  // categories is preserved so we can route the reference-shape rows through
  // the existing <ReferenceTab /> component while the rest use <AskListTab />.
  const availableTabs = useMemo(() => {
    if (!listStatus) return [];
    const cats = listStatus.categories || [];
    return cats
      .filter((c) => (c.count || 0) > 0)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [listStatus]);

  const [tabId, setTabId] = useState(null);
  // Deep-link support: when All Individuals routes here with a
  // `?category_id=X&donor_id=Y` URL, honor it by opening the target list
  // and stashing the donor id so <AskListTab> can auto-select on mount.
  // We clear the params IMMEDIATELY on consumption so a refresh doesn't
  // re-snap to that donor (per the acceptance test).
  const [searchParams, setSearchParams] = useSearchParams();
  const [pendingDonorId, setPendingDonorId] = useState(null);
  useEffect(() => {
    const catId = searchParams.get("category_id");
    if (!catId) return;
    const donorId = searchParams.get("donor_id");
    setTabId(catId);
    setPendingDonorId(donorId || null);
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams]);

  const refreshListStatus = () => {
    api.get("/caller/list-status")
      .then(({ data }) => setListStatus(data))
      .catch(() => setListStatus((s) => s || { active: 0, house: 0, reference: 0, categories: [], all_donors: 0 }));
  };

  useEffect(() => {
    let cancelled = false;
    api.get("/caller/list-status")
      .then(({ data }) => { if (!cancelled) setListStatus(data); })
      .catch(() => { if (!cancelled) setListStatus({ active: 0, house: 0, reference: 0, categories: [], all_donors: 0 }); });
    return () => { cancelled = true; };
  }, []);

  // Returning to the landing refreshes counts — All Donors actions
  // (add/remove memberships) change per-list counts.
  useEffect(() => {
    if (tabId === null && listStatus) refreshListStatus();
  }, [tabId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Feb 2026 UX pivot: replace the top tab row with a landing selection
  // screen. On login, the caller sees a big-target grid of categories with
  // names + counts. Tapping a card enters the list; "Back to call lists"
  // returns them here. A one-list campaign auto-skips the landing ONCE so
  // the single-list caller isn't forced through an extra tap — but they can
  // still go Back to reach All Donors.
  const autoSkippedRef = useRef(false);
  useEffect(() => {
    if (availableTabs.length === 1 && tabId === null && !autoSkippedRef.current) {
      autoSkippedRef.current = true;
      setPendingDonorId(null);
      setTabId(availableTabs[0].id);
    }
    if (tabId && tabId !== "__all__" && !availableTabs.some((t) => t.id === tabId)) {
      // The list this caller was on disappeared (deleted / renamed). Send
      // them back to the landing so they pick again.
      setPendingDonorId(null);
      setTabId(null);
    }
  }, [availableTabs, tabId]);

  const activeTab = tabId === "__all__" ? null : availableTabs.find((t) => t.id === tabId);
  // The landing is always reachable (it hosts All Donors), so the working
  // views always offer a back button.
  const showBack = true;
  const onBack = () => { setPendingDonorId(null); setTabId(null); };
  // Belt-and-suspenders: any list the user opens BY HAND (landing / list
  // picker / All-Donors card) must never carry a pending donor id from a
  // prior deep link. Deep-link consumption clears the id via the AskListTab
  // callback, but if that path somehow fails to fire, this ensures a
  // hand-picked list is always clean.
  const pickTab = (id) => { setPendingDonorId(null); setTabId(id); };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="caller-root">
      {/* Persistent shell header */}
      <div className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-md sm:max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
          <div className="min-w-0">
            <div className="text-[11px] font-bold uppercase tracking-wider text-flag-red">Caller Mode</div>
            <div className="font-display font-black text-flag-navy truncate text-lg sm:text-xl" data-testid="caller-candidate-name">
              {user?.name || ""}
            </div>
          </div>
          <div className="flex items-center gap-3 sm:gap-4 shrink-0">
            <AllTimeTracker refreshKey={`${tabId ?? ""}|${listStatus?.categories?.length ?? 0}|${listStatus?.active ?? 0}`} />
            <Link
              to="/caller/settings"
              className="inline-flex items-center gap-1 text-sm font-semibold text-slate-600 hover:text-flag-navy"
              data-testid="caller-settings-link"
            >
              <SettingsIcon className="h-4 w-4" /> Settings
            </Link>
            <button
              type="button"
              onClick={logout}
              className="inline-flex items-center gap-1 text-sm font-semibold text-slate-600 hover:text-flag-red"
              data-testid="caller-logout"
            >
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>
        </div>
      </div>

      {/* No lists at all — same empty state as before */}
      {listStatus && availableTabs.length === 0 && (
        <div className="max-w-md sm:max-w-lg mx-auto px-4 py-10 text-center" data-testid="caller-no-lists">
          <div className="text-flag-navy font-display text-xl font-black mb-2">No donor list uploaded yet</div>
          <p className="text-sm text-slate-600 leading-relaxed">
            Your list will appear here as soon as it&rsquo;s uploaded. If you were expecting to see donors, contact your campaign admin.
          </p>
        </div>
      )}

      {/* Landing selection — big finger-friendly cards, one per category */}
      {listStatus && availableTabs.length > 0 && tabId === null && (
        <CallerListLanding
          tabs={availableTabs}
          onPick={pickTab}
          allDonorsCount={listStatus.all_donors || 0}
        />
      )}

      {tabId === "__all__" && (
        <AllDonorsTab onBack={onBack} />
      )}

      {activeTab && activeTab.list_type_legacy === "reference" && (
        <ReferenceTab key={activeTab.id} categoryId={activeTab.id} onBack={showBack ? onBack : null} />
      )}
      {activeTab && activeTab.list_type_legacy !== "reference" && (
        <AskListTab
          key={activeTab.id}
          categoryId={activeTab.id}
          listType={activeTab.list_type_legacy || "house"}
          categoryType={activeTab.category_type || "call_list"}
          categoryName={activeTab.name || ""}
          onBack={showBack ? onBack : null}
          campaignConfig={campaignConfig}
          initialDonorId={pendingDonorId}
          onInitialDonorConsumed={() => setPendingDonorId(null)}
        />
      )}
    </div>
  );
}

/* --------------------------------------------------------------------- */
/**
 * Landing selection screen — replaces the top tab row (Feb 2026).
/**
 * Landing screen: "Which list are you working today?"
 *
 * Grouped by type so a non-technical user on a phone can scan by KIND
 * before name:
 *   Desktop (≥lg): two columns side-by-side. LEFT = Call Lists + PAC
 *     (both are "outreach" lists — one has a $7k cap, the other doesn't;
 *     grouping them keeps callable-work in one place). RIGHT = Prospecting.
 *   Mobile: single column. Call Lists first, then Prospecting — the
 *     grouping is preserved vertically. Large tap targets throughout.
 * Below the columns: two full-width cards — Maxed Donors first, then
 * "All Individuals" (renamed from All Donors) at the very bottom.
 * Big tappable cards, one per category, with donor counts. Sized for a
 * non-technical / older user on a phone: min 96px tap height, 20px name,
 * generous spacing.
 */

/** Badge look is driven ENTIRELY by category type — never per-card —
 * so every new list of a given type inherits the right color, and the
 * previous per-index cycle of colors on Prospecting lists can't reappear. */
const LANDING_BADGE_BY_TYPE = {
  call_list:        { cls: "bg-flag-red text-white",         label: "Call List" },
  pac_list:         { cls: "bg-yellow-500 text-flag-navy",   label: "PAC List" },
  prospecting_list: { cls: "bg-flag-navy text-white",        label: "Prospecting" },
  maxed:            { cls: "bg-flag-red text-white",         label: "Maxed Out" },
};

function LandingCard({ tab, onPick, dashed = false, testId }) {
  const type = tab.category_type || "call_list";
  const badge = LANDING_BADGE_BY_TYPE[type] || LANDING_BADGE_BY_TYPE.call_list;
  return (
    <button
      type="button"
      onClick={() => onPick(tab.id)}
      data-testid={testId || `caller-landing-card-${tab.id}`}
      className={`w-full text-left px-4 py-4 min-h-[96px] rounded-xl border-2 bg-white hover:border-flag-navy hover:shadow-lg active:scale-[0.99] transition-all flex items-center justify-between gap-3 ${dashed ? "border-dashed border-slate-300" : "border-slate-200"}`}
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${badge.cls}`}>
            {badge.label}
          </span>
        </div>
        <div className="mt-1 font-display text-lg sm:text-xl font-black text-flag-navy truncate">
          {tab.name}
        </div>
        <div className="text-sm text-slate-600 mt-0.5" data-testid={`caller-landing-count-${tab.id}`}>
          {tab.count.toLocaleString()} donor{tab.count === 1 ? "" : "s"}
        </div>
      </div>
      <ChevronRight className="h-6 w-6 text-slate-400 shrink-0" />
    </button>
  );
}

function CallerListLanding({ tabs, onPick, allDonorsCount = 0 }) {
  // Partition into: outreach column (Call + PAC), prospecting column,
  // and the two floor cards (Maxed, plus the synthetic "All Individuals"
  // handled below). Maxed always renders last-of-the-real-tabs as a
  // full-width card so it doesn't distract from active work.
  const outreach = [];
  const prospecting = [];
  const maxed = [];
  for (const t of tabs) {
    const type = t.category_type || "call_list";
    if (type === "prospecting_list") prospecting.push(t);
    else if (type === "maxed") maxed.push(t);
    else outreach.push(t);   // call_list + pac_list share the outreach column
  }
  return (
    <div className="max-w-md sm:max-w-lg lg:max-w-5xl mx-auto px-4 lg:px-6 py-6 lg:py-10" data-testid="caller-landing">
      <div className="text-center mb-6">
        <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Step 1</div>
        <h1 className="font-display text-2xl sm:text-3xl font-black text-flag-navy mt-0.5">
          Which list are you working today?
        </h1>
        <p className="text-sm text-slate-600 mt-1">Tap a list to start calling.</p>
      </div>

      {/* Two grouped columns — Call/PAC left, Prospecting right. On mobile
          they stack in that same order so the grouping is preserved. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 lg:gap-4">
        <div className="space-y-3" data-testid="caller-landing-column-outreach">
          {outreach.map((t) => (<LandingCard key={t.id} tab={t} onPick={onPick} />))}
        </div>
        <div className="space-y-3" data-testid="caller-landing-column-prospecting">
          {prospecting.map((t) => (<LandingCard key={t.id} tab={t} onPick={onPick} />))}
        </div>
      </div>

      {/* Floor: Maxed Donors, then All Individuals — both full-width and
          spanning both columns on desktop. */}
      {maxed.map((t) => (
        <div key={t.id} className="mt-3 lg:mt-4">
          <LandingCard tab={t} onPick={onPick} />
        </div>
      ))}
      {allDonorsCount > 0 && (
        <button
          type="button"
          onClick={() => onPick("__all__")}
          data-testid="caller-landing-all-donors"
          className="mt-3 lg:mt-4 w-full text-left px-4 py-4 min-h-[96px] rounded-xl border-2 border-dashed border-slate-300 bg-white hover:border-flag-navy hover:shadow-lg active:scale-[0.99] transition-all flex items-center justify-between gap-3"
        >
          <div className="min-w-0">
            <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-slate-200 text-slate-700">
              Everyone
            </span>
            <div className="mt-1 font-display text-lg sm:text-xl font-black text-flag-navy">All Individuals</div>
            <div className="text-sm text-slate-600 mt-0.5" data-testid="caller-landing-all-donors-count">
              {allDonorsCount.toLocaleString()} individual{allDonorsCount === 1 ? "" : "s"} across all lists
            </div>
          </div>
          <ChevronRight className="h-6 w-6 text-slate-400 shrink-0" />
        </button>
      )}
    </div>
  );
}

/* --------------------------------------------------------------------- */
/**
 * Tab 1 — "Need to Donate": the active call list. All existing behavior
 * (pre-drafted message, call/text/email/copy, outcomes, notes, next/prev).
 */
function AskListTab({ listType = "house", categoryId = null, categoryType = "call_list", categoryName = "", onBack = null, campaignConfig = null, initialDonorId = null, onInitialDonorConsumed = null }) {
  const isProspecting = categoryType === "prospecting_list";
  const isPac = categoryType === "pac_list";
  const isMaxedList = categoryType === "maxed";
  const isDesktop = useIsDesktop();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [lastImportAt, setLastImportAt] = useState(null);
  const [fieldAvailability, setFieldAvailability] = useState({});
  // Sort choice for the current list. "default" preserves the server-sent
  // order (bucketed by status, then by last_gift_date desc). Session-only
  // so a fresh login lands on the default; users who prefer a specific
  // sort re-pick it once per session.
  const [sortBy, setSortBy] = useState("default");
  // Session-scoped "hide maxed" toggle: maxed donors STAY on the list
  // (membership is truth), but during a calling session the caller can
  // filter them out to see only donors who can still give.
  const [hideMaxed, setHideMaxed] = useState(false);
  const [addDonorOpen, setAddDonorOpen] = useState(false);
  // Session progress — counted client-side from the outcomes logged this
  // session (survives list reloads within the same category; resets when
  // the category / list changes). Feb 2026 addition: motivational strip
  // above the list so a long calling session feels tangible.
  const [sessionStats, setSessionStats] = useState({ reached: 0, pledged_total: 0 });
  useEffect(() => {
    // Reset when the caller switches lists/categories — a "session" is
    // scoped to the current working list. Sort choice is likewise scoped
    // to the current list; picking a new category returns to Default.
    setSessionStats({ reached: 0, pledged_total: 0 });
    setSortBy("default");
  }, [categoryId, listType]);

  const handleOutcomeLogged = useCallback((info) => {
    if (!info) return;
    setSessionStats((s) => {
      const next = { ...s };
      if (info.outcome === "reached" || info.outcome === "pledged") next.reached += 1;
      if (info.outcome === "pledged" && Number.isFinite(Number(info.pledge_amount))) {
        next.pledged_total += Number(info.pledge_amount);
      }
      return next;
    });
    load();
  }, [categoryId, listType]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      params.set("filter", filter);
      // Category-scoped: when we have a category_id, the backend routes by it
      // and ignores `list`. We still send `list` for legacy back-compat.
      if (categoryId) params.set("category_id", categoryId);
      params.set("list", listType);
      if (hideMaxed && !isMaxedList) params.set("hide_maxed", "true");
      const { data } = await api.get(`/caller/donors?${params.toString()}`);
      setRows(data.donors || []);
      setLastImportAt(data.last_import_at || null);
      setFieldAvailability(data.field_availability || {});
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  }, [q, filter, categoryId, listType, hideMaxed, isMaxedList]);

  // `q` (search) is intentionally excluded here — it flows through the
  // debounced effect below. This one refires on filter/list/toggle switches.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filter, listType, hideMaxed]);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q, listType]);

  // Deep-link consumer: when All Individuals routes here via a chip click,
  // parent CallerMode passes the donor id in `initialDonorId`. Once rows
  // load, auto-select that donor so their detail pane opens on the right
  // and scroll them into view on the left. useRef guards single-shot
  // consumption per mount — a fresh remount (new categoryId, new key) resets
  // it. If the donor isn't in the list (e.g. soft-removed), toast a
  // non-blocking notice and stay on the normal list view.
  //
  // CRITICAL: we invoke `onInitialDonorConsumed` on BOTH branches (found
  // AND not-found) so the parent clears `pendingDonorId` immediately. Because
  // AskListTab remounts on every list switch (key={activeTab.id}), the ref
  // resets to false — if we didn't tell the parent to drop the id, a later
  // hand-picked list would receive the stale id as a prop and re-run this
  // effect against the wrong list, causing spurious selection or a spurious
  // "Couldn't open that donor in this list" toast.
  const initialDonorConsumedRef = useRef(false);
  useEffect(() => {
    if (initialDonorConsumedRef.current) return;
    if (!initialDonorId || loading || rows.length === 0) return;
    initialDonorConsumedRef.current = true;
    const idx = rows.findIndex((r) => r.id === initialDonorId);
    if (idx >= 0) {
      setSelectedIndex(idx);
      setTimeout(() => {
        const el = document.querySelector(`[data-testid="caller-donor-card-${initialDonorId}"]`);
        if (el && typeof el.scrollIntoView === "function") {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 100);
    } else {
      toast.info("Couldn't open that donor in this list.");
    }
    if (typeof onInitialDonorConsumed === "function") onInitialDonorConsumed();
  }, [initialDonorId, loading, rows, onInitialDonorConsumed]);

  const total = rows.length;
  const isActive = listType === "active";

  // Compute the sorted view. All sort options work on the client — the
  // server response is small enough that re-sorting locally on toggle
  // is instant and avoids a round-trip on every sort change. The
  // "default" case keeps the server-supplied order (status bucket +
  // last-gift-date desc) so the initial paint matches the backend spec.
  const sortedRows = useMemo(() => {
    if (sortBy === "default") return rows;
    const arr = rows.slice();
    const collator = new Intl.Collator(undefined, { sensitivity: "base" });
    if (sortBy === "alpha") {
      arr.sort((a, b) => {
        const la = (a.last_name || "").trim();
        const lb = (b.last_name || "").trim();
        const cmp = collator.compare(la, lb);
        if (cmp !== 0) return cmp;
        return collator.compare(a.first_name || "", b.first_name || "");
      });
    } else if (sortBy === "room") {
      arr.sort((a, b) => (b.general_room || 0) - (a.general_room || 0));
    } else if (sortBy === "last_gift") {
      // Newest gift first; donors with no last_gift_date sink to the bottom.
      arr.sort((a, b) => {
        const da = a.last_gift_date || "";
        const db = b.last_gift_date || "";
        if (da === db) return 0;
        if (!da) return 1;
        if (!db) return -1;
        return db.localeCompare(da);
      });
    } else if (sortBy === "total") {
      arr.sort((a, b) => (b.total_given || 0) - (a.total_given || 0));
    }
    return arr;
  }, [rows, sortBy]);

  // Stable callbacks so React.memo'd row children don't invalidate on
  // every list re-render (previously each row recreated its onClick on
  // every keystroke / selection change).
  const handleSelect = useCallback((idx) => setSelectedIndex(idx), []);
  const handlePrev = useCallback(() => setSelectedIndex((i) => Math.max(0, (i ?? 0) - 1)), []);
  const handleNext = useCallback(
    () => setSelectedIndex((i) => (i !== null && i < sortedRows.length - 1 ? i + 1 : i)),
    [sortedRows.length]
  );
  const handleClose = useCallback(() => setSelectedIndex(null), []);
  // Manual "Move to Maxed Donors" completed → close detail + refresh list.
  const handleMovedToMaxed = useCallback(() => {
    setSelectedIndex(null);
    load();
  }, [load]);
  const blurbTitle = isMaxedList
    ? "Maxed Donors"
    : isProspecting
      ? `${categoryName || "Prospecting List"} — New Donor Prospects`
      : isActive
        ? "Active Donors"
        : "House File Donors";
  const blurbBody = isMaxedList
    ? "These donors have reached the $7,000 federal limit for this cycle. Thank them — they can't give more until next cycle."
    : isProspecting
      ? "Prospects to reach out to. These donors haven't given yet — introduce yourself and make the ask."
      : isActive
        ? "Your best asks. They already give to other Republicans and can still give to you. See who they support below their name."
        : "Your own donors. Use \u201CHide maxed\u201D to see only those who can still give this cycle.";
  const accent = isActive ? "flag-red" : "flag-navy";
  const emptyMsg = isProspecting
    ? "No prospects loaded yet. Ask your admin to upload a prospecting list."
    : isActive
      ? "No Active donors loaded yet. Ask your admin to upload the Active Donors file."
      : "No House-file donors loaded yet. Ask your admin to import your donor list.";

  return (
    <div data-testid="caller-tab-body-ask" className="lg:h-[calc(100vh-var(--caller-header,220px))]">
      <div className="lg:grid lg:grid-cols-[minmax(360px,420px)_1fr] lg:h-full">
        {/* Left pane — list. Persistent on desktop, single column on mobile. */}
        <div className="lg:h-full lg:overflow-y-auto lg:border-r lg:border-slate-200 lg:bg-white">
          {/* Tab-specific sticky search/filter */}
          <div className="sticky top-0 z-10 bg-white border-b border-slate-200">
            <div className="max-w-md lg:max-w-none mx-auto lg:mx-0 px-4 lg:px-4 py-3 space-y-2">
              {/* Back to call lists + current list name — Feb 2026 landing UX.
                  Rendered only when >=2 lists exist; a 1-list caller doesn't
                  need to back out to a landing they never saw. */}
              {(onBack || categoryName) && (
                <div className="flex items-center gap-2" data-testid="caller-tab-header">
                  {onBack && (
                    <button
                      type="button"
                      onClick={onBack}
                      data-testid="caller-back-to-lists"
                      className="inline-flex items-center gap-1 h-8 px-2 -ml-2 text-xs font-bold text-flag-navy hover:bg-slate-100 rounded-md"
                    >
                      <ArrowLeft className="h-3.5 w-3.5" />
                      Back to call lists
                    </button>
                  )}
                  {categoryName && (
                    <div className="ml-auto text-xs font-bold uppercase tracking-wider text-slate-500 truncate max-w-[45%] text-right" data-testid="caller-current-list-name">
                      {categoryName}
                    </div>
                  )}
                </div>
              )}
              <div className="flex items-center justify-between">
                <div className="text-xs text-slate-500" data-testid="caller-total">
                  Donors to call: <strong className="text-flag-navy">{total.toLocaleString()}</strong>
                </div>
                {lastImportAt && (
                  <div className="text-[10px] text-slate-400" data-testid="ask-last-updated">
                    Last updated {fmtDate(lastImportAt)}
                  </div>
                )}
              </div>
              {/* Session progress strip — one line, motivational, always
                  reflects the current sitting. Position advances with the
                  detail-pane selection; reached/pledged counts increment
                  as outcomes are logged. */}
              <SessionProgressStrip
                position={selectedIndex !== null ? selectedIndex + 1 : 0}
                total={total}
                reached={sessionStats.reached}
                pledgedTotal={sessionStats.pledged_total}
              />
              <div className="relative">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search by name, city, employer, or ZIP"
                  className="h-10 pl-9 bg-slate-50 border-slate-200"
                  data-testid="caller-search"
                />
              </div>
              {!isMaxedList && (
                <button
                  type="button"
                  onClick={() => setAddDonorOpen(true)}
                  className="inline-flex items-center justify-center gap-1 h-9 text-sm font-semibold text-white bg-flag-navy hover:bg-flag-navy/90 rounded-md"
                  data-testid="caller-add-donor-btn"
                >
                  <Plus className="h-4 w-4" /> Add donor to this list
                </button>
              )}
              <div className="flex gap-1.5">
                {FILTERS.map((f) => (
                  <button
                    key={f.key}
                    type="button"
                    onClick={() => setFilter(f.key)}
                    data-testid={`caller-filter-${f.key}`}
                    className={`flex-1 px-2 py-1.5 text-xs font-bold rounded-md transition-colors ${
                      filter === f.key
                        ? "bg-flag-navy text-white"
                        : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
              {/* Sort control. Prospecting lists have no giving data, so we
                  only expose Alphabetical there — hiding sorts that would
                  order by all-zero fields (mirrors the same treatment as
                  the empty-field hiding elsewhere). */}
              <div className="flex items-center gap-2 pt-1">
                <label htmlFor="caller-sort" className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Sort</label>
                <select
                  id="caller-sort"
                  value={sortBy}
                  onChange={(e) => { setSortBy(e.target.value); setSelectedIndex(null); }}
                  data-testid="caller-sort-select"
                  className="flex-1 h-8 px-2 rounded-md border border-slate-300 bg-white text-xs font-semibold text-flag-navy focus:border-flag-navy focus:outline-none"
                >
                  <option value="default">Default (recent activity)</option>
                  <option value="alpha">Alphabetical (A–Z last name)</option>
                  {!isProspecting && <option value="room">Remaining room (highest first)</option>}
                  {!isProspecting && <option value="last_gift">Last gift date (most recent)</option>}
                  {!isProspecting && <option value="total">Total given (highest first)</option>}
                </select>
              </div>
              {/* Hide-maxed toggle — session filter only. Maxed donors keep
                  their list membership (counts include them); this just
                  hides them from the working view while dialing. Hidden
                  entirely on the Maxed tab (everyone's maxed there by
                  definition) and on PAC lists (no cap applies — no
                  maxed status). */}
              {!isMaxedList && !isPac && (
                <label
                  className="flex items-center gap-2 pt-1 cursor-pointer select-none"
                  data-testid="caller-hide-maxed-toggle"
                >
                  <input
                    type="checkbox"
                    checked={hideMaxed}
                    onChange={(e) => { setHideMaxed(e.target.checked); setSelectedIndex(null); }}
                    className="h-4 w-4 accent-flag-red"
                    data-testid="caller-hide-maxed-checkbox"
                  />
                  <span className="text-xs font-bold text-slate-600">
                    Hide maxed donors (at the $7,000 cycle limit)
                  </span>
                </label>
              )}
            </div>
          </div>

          <main className="max-w-md lg:max-w-none mx-auto lg:mx-0 px-4 py-4">
            <div
              className={`mb-4 border-l-4 rounded-r-lg px-4 py-3 ${
                isActive ? "bg-flag-red/5 border-flag-red" : "bg-flag-navy/5 border-flag-navy"
              }`}
              data-testid={`${listType}-tab-blurb`}
            >
              <div className="text-base sm:text-lg font-bold text-flag-navy">{blurbTitle}</div>
              <p className="text-sm sm:text-base text-slate-700">{blurbBody}</p>
            </div>
            {loading && <div className="text-center text-slate-500 py-10 text-base" data-testid="caller-loading">Loading donors&hellip;</div>}
            {!loading && rows.length === 0 && (
              <div className="bg-white border border-dashed border-slate-300 rounded-xl p-8 text-center text-slate-500 text-base" data-testid="caller-empty">
                {q ? "No donors match that search." : filter !== "all" ? "No donors in this filter." : emptyMsg}
              </div>
            )}
            <DonorList
              rows={sortedRows}
              selectedIndex={selectedIndex}
              onSelect={handleSelect}
              isProspecting={isProspecting}
              isPac={isPac}
              isActive={isActive}
              campaignConfig={campaignConfig}
            />
          </main>
        </div>

        {/* Right pane — desktop only. Mounted only when the viewport is
            actually `lg+` so its useEffect doesn't run in a hidden branch. */}
        {isDesktop && (
          <div className="lg:h-full lg:overflow-y-auto bg-slate-50">
            {selectedIndex !== null && sortedRows[selectedIndex] ? (
              <DonorDetail
                key={sortedRows[selectedIndex].id}
                layout="inline"
                donor={sortedRows[selectedIndex]}
                fieldAvailability={fieldAvailability}
                position={selectedIndex + 1}
                total={sortedRows.length}
                hasPrev={selectedIndex > 0}
                hasNext={selectedIndex < sortedRows.length - 1}
                onPrev={handlePrev}
                onNext={handleNext}
                onClose={handleClose}
                onStatusSaved={handleOutcomeLogged}
                categoryType={categoryType}
                categoryId={categoryId}
                onMovedToMaxed={handleMovedToMaxed}
                campaignConfig={campaignConfig}
              />
            ) : (
              <div
                className="h-full flex items-center justify-center text-center px-8 py-16 text-slate-400"
                data-testid="caller-detail-empty-pane"
              >
                <div>
                  <div className="font-display text-xl font-bold text-slate-500 mb-1">Select a donor</div>
                  <p className="text-sm">Choose a name from the list on the left to open their card here.</p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Mobile modal overlay — mounted only below `lg` so both DonorDetails
          never mount side-by-side (prior to Feb 2026 both mounted and each
          fired its own detail fetch on every click). */}
      {!isDesktop && selectedIndex !== null && sortedRows[selectedIndex] && (
        <DonorDetail
          key={sortedRows[selectedIndex].id}
          layout="modal"
          donor={sortedRows[selectedIndex]}
          categoryId={categoryId}
          fieldAvailability={fieldAvailability}
          position={selectedIndex + 1}
          total={sortedRows.length}
          hasPrev={selectedIndex > 0}
          hasNext={selectedIndex < sortedRows.length - 1}
          onPrev={handlePrev}
          onNext={handleNext}
          onClose={handleClose}
          onStatusSaved={handleOutcomeLogged}
          categoryType={categoryType}
          onMovedToMaxed={handleMovedToMaxed}
          campaignConfig={campaignConfig}
        />
      )}
      <AddDonorDialog
        open={addDonorOpen}
        onClose={() => setAddDonorOpen(false)}
        lists={[{ id: categoryId, name: categoryName, category_type: categoryType }]}
        defaultCategoryId={categoryId}
        lockCategory={true}
        onCreated={(donor) => {
          load().then(() => {
            setRows((prev) => {
              const idx = prev.findIndex((r) => r.id === donor.id);
              if (idx >= 0) setSelectedIndex(idx);
              return prev;
            });
          });
        }}
        onJoined={({ donor_id }) => {
          load().then(() => {
            setRows((prev) => {
              const idx = prev.findIndex((r) => r.id === donor_id);
              if (idx >= 0) setSelectedIndex(idx);
              return prev;
            });
          });
        }}
      />
    </div>
  );
}

/* --------------------------------------------------------------------- */
/**
 * Single donor card in the list. Wrapped in React.memo so re-renders on
 * selection change only touch the two rows whose `isSelected` flipped —
 * not all 400+ rows. Callback is stable via useCallback in the parent.
 */
const DonorRow = memo(function DonorRow({ donor: d, index, isSelected, isProspecting, isPac, isActive, onSelect, campaignConfig = null }) {
  const isDone = d.status === "pledged" || d.status === "declined";
  const townState = [d.city, d.state].filter(Boolean).join(", ");
  // Giving-history subline. Preference order — each falls back to the next
  // when the primary field is empty:
  //   1. `last_gift_amount` + `last_gift_date` — clean per-row export shape.
  //   2. `highest_gift` — the file gave us a max-single-gift but no last-gift
  //      timestamp (common for maxed donors imported from a summary sheet).
  //   3. `2026 Primary + 2026 General` — the cycle buckets. Populated on the
  //      failing "No prior gift on file" rows even when the flat gift fields
  //      are blank. A maxed donor MUST have this if nothing else, since
  //      the retroactive sweep and the badge both key off exactly this sum.
  //   4. `cycle_to_date` — last-ditch fallback.
  // A maxed donor can never fall all the way through to "No prior gift on
  // file" — one of the fallbacks always fires because that's the same
  // signal the sweep used to flag them maxed.
  const cycleGiving = (Number(d.primary_2026) || 0) + (Number(d.general_2026) || 0);
  let givingLine = "No prior gift on file";
  if (d.last_gift_amount > 0 || d.last_gift_date) {
    givingLine = `Last gift ${fmtMoney(d.last_gift_amount)} · ${fmtDate(d.last_gift_date)}`;
  } else if (d.highest_gift > 0) {
    givingLine = `Highest gift ${fmtMoney(d.highest_gift)}`;
  } else if (cycleGiving > 0) {
    givingLine = `This cycle ${fmtMoney(cycleGiving)}`;
  } else if (d.cycle_to_date > 0) {
    givingLine = `Cycle-to-date ${fmtMoney(d.cycle_to_date)}`;
  }
  const subline = isProspecting ? (townState || "—") : givingLine;
  const committees = d.committee_giving || [];
  // PAC + Prospecting both hide the "Can give" right-rail and the MAXED
  // badge — same reasons as on the detail card (no cap on PAC; no
  // giving data on Prospecting).
  const hideCap = isProspecting || isPac;
  // State-matching branch mirrors the DonorDetail contract EXACTLY so the
  // left-card figure and the detail-pane MATCHABLE ROOM figure agree per
  // donor. Federal accounts never evaluate this to true.
  const isStateMatching = campaignConfig?.campaign_type === "state_matching";
  const smActive = isStateMatching && !isProspecting && !isPac;
  const matchableCeiling = Number(campaignConfig?.matchable_ceiling ?? 250) || 0;
  const smMatchableRoom = Math.max(0, Math.floor(matchableCeiling - cycleGiving));
  return (
    <button
      type="button"
      onClick={() => onSelect(index)}
      className={`w-full text-left bg-white border rounded-xl px-4 py-4 shadow-sm hover:shadow-md transition-shadow ${
        isDone ? "opacity-60" : ""
      } ${isSelected ? "lg:border-flag-navy lg:border-2 lg:shadow-md" : "border-slate-200"}`}
      data-testid={`caller-donor-card-${d.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-display font-bold text-flag-navy text-xl sm:text-2xl truncate" data-testid={`caller-donor-name-${d.id}`}>
            {d.first_name} {d.last_name}
            {d.maxed && !isPac && !isProspecting && (
              <span
                className="ml-2 align-middle text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-flag-red text-white"
                data-testid={`caller-donor-maxed-badge-${d.id}`}
              >
                Maxed
              </span>
            )}
            {isProspecting && d.total_given > 0 && (
              <span
                className="ml-2 align-middle text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-600 text-white whitespace-nowrap"
                data-testid={`caller-donor-already-donating-badge-${d.id}`}
              >
                Already Donating
              </span>
            )}
          </div>
          <div
            className="text-sm sm:text-base text-slate-600 mt-0.5"
            data-testid={isProspecting ? `caller-donor-town-state-${d.id}` : `caller-donor-last-gift-${d.id}`}
          >
            {subline}
          </div>
        </div>
        {!hideCap && !smActive && (
          <div className="text-right shrink-0">
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Can give</div>
            <div className="font-display text-2xl sm:text-3xl font-black text-flag-red" data-testid={`caller-donor-room-${d.id}`}>
              {fmtMoney(d.general_room)}
            </div>
          </div>
        )}
        {!hideCap && smActive && (
          // State-matching sibling of the "Can give" cell above. Reads the
          // SAME formula and the SAME campaignConfig as the detail pane so
          // the two views can never drift per donor. Federal path never
          // reaches this branch.
          <div className="text-right shrink-0">
            <div className="text-[11px] font-bold uppercase tracking-wider text-flag-navy">Match room</div>
            <div
              className="font-display text-2xl sm:text-3xl font-black text-flag-navy"
              data-testid={`caller-donor-match-room-${d.id}`}
            >
              {fmtMoney(smMatchableRoom)}
            </div>
          </div>
        )}
      </div>
      {!isProspecting && isActive && committees.length > 0 && (
        <div className="mt-3 pt-3 border-t border-slate-100" data-testid={`caller-donor-committees-${d.id}`}>
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">Also gives to</div>
          <ul className="text-sm sm:text-base text-slate-700 leading-relaxed">
            {committees.map((c) => (
              <li key={c.label} className="flex items-center justify-between">
                <span>{c.label}</span>
                <strong className="text-flag-navy tabular-nums">{fmtMoney(c.amount)}</strong>
              </li>
            ))}
          </ul>
        </div>
      )}
    </button>
  );
});

/**
 * Donor list container. Under 200 rows we render a plain <ul> (the
 * memoized DonorRow keeps selection re-renders cheap). At/above 200 rows
 * we switch to react-window virtualization so only the visible slice of
 * cards mounts — critical for 400-500 donor prospecting lists like
 * Monique DeSpain's Prospecting File.
 */
const VIRTUALIZE_THRESHOLD = 200;

function DonorList({ rows, selectedIndex, onSelect, isProspecting, isPac, isActive, campaignConfig = null }) {
  if (rows.length === 0) return null;
  if (rows.length < VIRTUALIZE_THRESHOLD) {
    return (
      <ul className="space-y-2.5">
        {rows.map((d, idx) => (
          <li key={d.id}>
            <DonorRow
              donor={d}
              index={idx}
              isSelected={selectedIndex === idx}
              isProspecting={isProspecting}
              isPac={isPac}
              isActive={isActive}
              onSelect={onSelect}
              campaignConfig={campaignConfig}
            />
          </li>
        ))}
      </ul>
    );
  }
  return (
    <VirtualDonorList
      rows={rows}
      selectedIndex={selectedIndex}
      onSelect={onSelect}
      isProspecting={isProspecting}
      isPac={isPac}
      isActive={isActive}
      campaignConfig={campaignConfig}
    />
  );
}

/**
 * Virtualized variant. Row heights vary (Active-tab rows can have up to
 * 8 committee lines). We estimate a conservative fixed height that fits
 * the typical prospecting row (name + subline + gutter). The heavier
 * Active layout uses committees which are usually paginated by category,
 * so mixing tabs and virtualization at scale doesn't over-clip.
 */
function VirtualDonorList({ rows, selectedIndex, onSelect, isProspecting, isPac, isActive, campaignConfig = null }) {
  const rowHeight = isActive ? 180 : 108;
  return (
    <div className="h-[calc(100vh-var(--caller-header,300px))] min-h-[400px]" data-testid="caller-virtual-list">
      <VirtualList
        rowCount={rows.length}
        rowHeight={rowHeight}
        defaultHeight={600}
        overscanCount={4}
        rowComponent={VirtualRow}
        rowProps={{ rows, selectedIndex, onSelect, isProspecting, isPac, isActive, campaignConfig }}
      />
    </div>
  );
}

function VirtualRow({ index, style, rows, selectedIndex, onSelect, isProspecting, isPac, isActive, campaignConfig = null }) {
  const d = rows[index];
  return (
    <div style={style} className="pb-2.5 pr-1">
      <DonorRow
        donor={d}
        index={index}
        isSelected={selectedIndex === index}
        isProspecting={isProspecting}
        isPac={isPac}
        isActive={isActive}
        onSelect={onSelect}
        campaignConfig={campaignConfig}
      />
    </div>
  );
}

/**
 * One-line motivational session strip. "Donor 12 of 47 · 8 reached ·
 * $4,500 pledged". Position and totals come from the list state;
 * reached/pledged are incremented as outcomes are logged during the
 * current sitting. Stays inside the sticky filter header so it's
 * always visible from anywhere in the list.
 */
function SessionProgressStrip({ position, total, reached, pledgedTotal }) {
  const posLabel = position > 0 && total > 0 ? `Donor ${position} of ${total}` : `${total} donor${total === 1 ? "" : "s"}`;
  const pledgedLabel = pledgedTotal > 0 ? `$${Math.round(pledgedTotal).toLocaleString()} pledged` : "$0 pledged";
  return (
    <div
      className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs sm:text-sm font-semibold text-flag-navy bg-flag-navy/5 border border-flag-navy/20 rounded-md px-3 py-1.5"
      data-testid="caller-session-strip"
    >
      <span className="text-[10px] font-bold uppercase tracking-wider text-flag-navy/70">This session</span>
      <span className="text-slate-400">·</span>
      <span data-testid="session-position">{posLabel}</span>
      <span className="text-slate-400">·</span>
      <span data-testid="session-reached"><strong>{reached}</strong> reached</span>
      <span className="text-slate-400">·</span>
      <span data-testid="session-pledged" className="text-emerald-700"><strong>{pledgedLabel}</strong></span>
    </div>
  );
}

/* --------------------------------------------------------------------- */
/**
 * Tab 2 — "Have Ever Donated": read-only lookup reference. No message,
 * no outcomes, no notes, no general-election-room framing. Just search
 * and open contact + history.
 */
function ReferenceTab({ categoryId = null, onBack = null }) {  // eslint-disable-line no-unused-vars
  // categoryId is currently unused — the reference collection is 1:1 with
  // the migrated "Have Ever Donated" category so scoping happens
  // automatically. Accepting the prop keeps the tab renderer symmetrical.
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [lastImportAt, setLastImportAt] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      const { data } = await api.get(`/caller/reference?${params.toString()}`);
      setRows(data.donors || []);
      setLastImportAt(data.last_import_at || null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  return (
    <div data-testid="caller-tab-body-reference">
      <div className="sticky top-0 z-10 bg-white border-b border-slate-200">
        <div className="max-w-md mx-auto px-4 py-3 space-y-2">
          {onBack && (
            <div className="flex items-center gap-2" data-testid="caller-tab-header">
              <button
                type="button"
                onClick={onBack}
                data-testid="caller-back-to-lists"
                className="inline-flex items-center gap-1 h-8 px-2 -ml-2 text-xs font-bold text-flag-navy hover:bg-slate-100 rounded-md"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                Back to call lists
              </button>
            </div>
          )}
          <div className="flex items-center justify-between">
            <div className="text-xs text-slate-500" data-testid="ref-total">
              {rows.length.toLocaleString()} donor{rows.length === 1 ? "" : "s"} on file
            </div>
            {lastImportAt && (
              <div className="text-[10px] text-slate-400" data-testid="ref-last-updated">
                Last updated {fmtDate(lastImportAt)}
              </div>
            )}
          </div>
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, city, employer, or ZIP"
              className="h-10 pl-9 bg-slate-50 border-slate-200"
              data-testid="reference-search"
            />
          </div>
        </div>
      </div>

      <main className="max-w-md sm:max-w-lg mx-auto px-4 py-4">
        <div className="mb-4 bg-slate-100 border-l-4 border-slate-500 rounded-r-lg px-4 py-3" data-testid="ref-tab-blurb">
          <div className="text-base sm:text-lg font-bold text-flag-navy">Have Ever Donated</div>
          <p className="text-sm sm:text-base text-slate-700">Everyone who has ever given. Look someone up here.</p>
        </div>
        {loading && <div className="text-center text-slate-500 py-10 text-base">Loading donors&hellip;</div>}
        {!loading && rows.length === 0 && (
          <div className="bg-white border border-dashed border-slate-300 rounded-xl p-8 text-center text-slate-500" data-testid="reference-empty">
            {q ? "No donors match that search." : "No reference donors imported yet. Ask your admin to upload the Have-Ever-Donated file."}
          </div>
        )}
        <ul className="space-y-2">
          {rows.map((d) => (
            <li key={d.id}>
              <button
                type="button"
                onClick={() => setSelectedId(d.id)}
                className="w-full text-left bg-white border border-slate-200 rounded-xl px-3 py-2.5 shadow-sm hover:shadow-md transition-shadow"
                data-testid={`reference-donor-card-${d.id}`}
              >
                <div className="flex items-baseline justify-between gap-2">
                  <div className="font-semibold text-flag-navy truncate">
                    {d.last_name}, {d.first_name}
                  </div>
                  {d.total_given > 0 && (
                    <div className="text-[11px] text-slate-500 shrink-0">
                      Lifetime {fmtMoney(d.total_given)}
                    </div>
                  )}
                </div>
                {(d.city || d.state) && (
                  <div className="text-[11px] text-slate-500 truncate">
                    {[d.city, d.state].filter(Boolean).join(", ")}{d.employer ? ` · ${d.employer}` : ""}
                  </div>
                )}
              </button>
            </li>
          ))}
        </ul>
      </main>

      {selectedId && (
        <ReferenceDetail donorId={selectedId} onClose={() => setSelectedId(null)} />
      )}
    </div>
  );
}

function ReferenceDetail({ donorId, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    api.get(`/caller/reference/${donorId}`)
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message));
    return () => { cancelled = true; };
  }, [donorId]);

  const d = data?.donor;

  return (
    <div className="fixed inset-0 z-30 bg-slate-50 overflow-y-auto" data-testid={`reference-detail-${donorId}`} role="dialog">
      <header className="sticky top-0 z-10 bg-white border-b border-slate-200">
        <div className="max-w-md mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center gap-1 text-sm font-semibold text-slate-600 hover:text-flag-navy"
            data-testid="reference-detail-back"
          >
            <ArrowLeft className="h-4 w-4" /> Back to lookup
          </button>
          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Reference · read-only</span>
        </div>
      </header>

      <main className="max-w-md mx-auto px-4 py-4 space-y-4 pb-16">
        {err && <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg px-3 py-2 text-sm">{err}</div>}
        {!d && !err && <div className="text-center text-slate-500 py-10">Loading donor…</div>}
        {d && (
          <>
            {/* Name */}
            <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Donor</div>
              <div className="font-display text-2xl font-black text-flag-navy" data-testid="reference-detail-name">
                {d.first_name} {d.last_name}
              </div>
              {(d.city || d.state) && (
                <div className="text-xs text-slate-500 mt-0.5">
                  {[d.address, [d.city, d.state].filter(Boolean).join(", ")].filter(Boolean).join(" · ")}
                  {d.zip ? ` ${d.zip}` : ""}
                </div>
              )}
            </div>

            {/* Contact — hide missing fields */}
            {(d.phone || d.email) && (
              <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">Contact</div>
                {d.phone && (
                  <a
                    href={`tel:${d.phone.replace(/[^\d+]/g, "")}`}
                    className="flex items-center justify-between py-1.5 text-sm hover:text-flag-red"
                    data-testid="reference-detail-phone"
                  >
                    <span className="inline-flex items-center gap-2 text-slate-500">
                      <Phone className="h-3.5 w-3.5" />
                      Phone{d.phone_type ? <span className="text-[10px] font-bold uppercase tracking-wider bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">{d.phone_type}</span> : null}
                    </span>
                    <span className="font-semibold text-flag-navy">{d.phone}</span>
                  </a>
                )}
                {d.email && (
                  <a
                    href={`mailto:${d.email}`}
                    className="flex items-center justify-between py-1.5 text-sm hover:text-flag-red border-t border-slate-100"
                    data-testid="reference-detail-email"
                  >
                    <span className="inline-flex items-center gap-2 text-slate-500">
                      <Mail className="h-3.5 w-3.5" /> Email
                    </span>
                    <span className="font-semibold text-flag-navy truncate max-w-[65%]">{d.email}</span>
                  </a>
                )}
              </div>
            )}

            {/* Work */}
            {(d.employer || d.occupation) && (
              <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Work</div>
                <div className="text-sm text-slate-800">{d.employer || "—"}</div>
                {d.occupation && <div className="text-xs text-slate-500">{d.occupation}</div>}
              </div>
            )}

            {/* Giving history — everything on file */}
            <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">Giving history</div>
              <dl className="grid grid-cols-2 gap-x-3 gap-y-2 text-sm">
                <dt className="text-slate-500">Last gift</dt>
                <dd className="text-right font-semibold text-flag-navy">
                  {d.last_gift_amount > 0 || d.last_gift_date
                    ? `${fmtMoney(d.last_gift_amount)} · ${fmtDate(d.last_gift_date)}`
                    : "—"}
                </dd>
                <dt className="text-slate-500">First gift</dt>
                <dd className="text-right font-semibold text-flag-navy">{fmtDate(d.first_gift_date)}</dd>
                <dt className="text-slate-500">Highest gift</dt>
                <dd className="text-right font-semibold text-flag-navy">
                  {d.highest_gift > 0 || d.highest_gift_date
                    ? `${fmtMoney(d.highest_gift)}${d.highest_gift_date ? ` · ${fmtDate(d.highest_gift_date)}` : ""}`
                    : "—"}
                </dd>
                <dt className="text-slate-500">Total given</dt>
                <dd className="text-right font-semibold text-flag-navy">{fmtMoney(d.total_given)}</dd>
                <dt className="text-slate-500">Number of gifts</dt>
                <dd className="text-right font-semibold text-flag-navy">{d.number_of_gifts}</dd>
                <dt className="text-slate-500">This cycle</dt>
                <dd className="text-right font-semibold text-flag-navy" data-testid="reference-detail-this-cycle">
                  {fmtMoney(d.this_cycle ?? ((d.primary_2026 || 0) + (d.general_2026 || 0)))}
                </dd>
              </dl>
            </div>
          </>
        )}
      </main>
    </div>
  );
}

/* --------------------------------------------------------------------- */

/* --------------------------------------------------------------------- */
/** Compact contact card with inline edit. Hand-edited fields become
 * upload-protected server-side; here we just decorate them with a small
 * lock icon so the caller knows they'll survive the next file. Prompts
 * "+ Add phone / + Add email" show when a donor has nothing on file
 * (common for Prospecting). */
function ContactBlock({ donor, onSaved, recordChannel }) {
  const d = donor || {};
  const locked = new Set(d.manual_contact_fields || []);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    phone: d.phone || "", phone_type: d.phone_type || "",
    phone2: d.phone2 || "", phone2_type: d.phone2_type || "",
    email: d.email || "",
  });

  const openEdit = () => {
    setForm({
      phone: d.phone || "", phone_type: d.phone_type || "",
      phone2: d.phone2 || "", phone2_type: d.phone2_type || "",
      email: d.email || "",
    });
    setEditing(true);
  };

  const save = async () => {
    setSaving(true);
    const orig = {
      phone: d.phone || "", phone_type: d.phone_type || "",
      phone2: d.phone2 || "", phone2_type: d.phone2_type || "",
      email: d.email || "",
    };
    const patch = {};
    for (const k of Object.keys(orig)) {
      if ((form[k] || "").trim() !== orig[k]) patch[k] = (form[k] || "").trim();
    }
    if (Object.keys(patch).length === 0) {
      setEditing(false); setSaving(false); return;
    }
    try {
      const { data } = await api.patch(`/caller/donors/${d.id}/contact`, patch);
      onSaved(data.donor);
      toast.success("Contact updated — protected from future uploads");
      setEditing(false);
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const hasAny = d.phone || d.email || d.phone2;

  if (editing) {
    return (
      <div className="bg-white border-2 border-flag-navy rounded-xl p-4 shadow-sm space-y-3"
           data-testid="caller-detail-contact-editor">
        <div className="flex items-center justify-between">
          <div className="text-xs font-bold uppercase tracking-wider text-flag-navy">Edit contact</div>
          <button type="button" onClick={() => setEditing(false)}
                  className="text-slate-500 hover:text-slate-700 p-1"
                  data-testid="caller-detail-contact-cancel">
            <CloseIcon className="h-4 w-4" />
          </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_120px] gap-2">
          <Input value={form.phone}
                 onChange={(e) => setForm({ ...form, phone: e.target.value })}
                 placeholder="Phone" inputMode="tel"
                 className="h-11 text-base" data-testid="caller-contact-phone-input" />
          <Input value={form.phone_type}
                 onChange={(e) => setForm({ ...form, phone_type: e.target.value })}
                 placeholder="Type (cell)" className="h-11 text-base"
                 data-testid="caller-contact-phone-type-input" />
          <Input value={form.phone2}
                 onChange={(e) => setForm({ ...form, phone2: e.target.value })}
                 placeholder="Phone 2 (optional)" inputMode="tel"
                 className="h-11 text-base" data-testid="caller-contact-phone2-input" />
          <Input value={form.phone2_type}
                 onChange={(e) => setForm({ ...form, phone2_type: e.target.value })}
                 placeholder="Type (work)" className="h-11 text-base"
                 data-testid="caller-contact-phone2-type-input" />
          <Input value={form.email}
                 onChange={(e) => setForm({ ...form, email: e.target.value })}
                 placeholder="name@example.com" inputMode="email"
                 className="h-11 text-base sm:col-span-2"
                 data-testid="caller-contact-email-input" />
        </div>
        <div className="flex items-center gap-2 pt-1">
          <Button onClick={save} disabled={saving}
                  className="h-11 text-base flex-1 bg-flag-navy hover:bg-flag-navy/90 text-white"
                  data-testid="caller-detail-contact-save">
            {saving ? "Saving…" : "Save"}
          </Button>
          <Button variant="outline" onClick={() => setEditing(false)} disabled={saving}
                  className="h-11 text-base">
            Cancel
          </Button>
        </div>
        <p className="text-[10.5px] text-slate-500 leading-snug flex items-start gap-1">
          <Lock className="h-3 w-3 mt-0.5 shrink-0" />
          Anything you save here is protected — future uploads won't overwrite it.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-3.5 shadow-sm"
         data-testid="caller-detail-contact">
      <div className="flex items-center justify-between mb-1">
        <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold">Contact</div>
        <button type="button" onClick={openEdit}
                className="inline-flex items-center gap-1 text-xs font-semibold text-flag-navy hover:text-flag-red"
                data-testid="caller-detail-contact-edit-btn">
          <Pencil className="h-3.5 w-3.5" /> Edit
        </button>
      </div>
      {d.phone && (
        <a href={`tel:${d.phone.replace(/[^\d+]/g, "")}`}
           onClick={() => recordChannel("call")}
           className="flex items-center justify-between py-1.5 text-base hover:text-flag-red"
           data-testid="caller-detail-phone">
          <span className="inline-flex items-center gap-2 text-slate-600 text-sm">
            <Phone className="h-4 w-4" />
            Phone
            {d.phone_type && <span className="text-xs font-bold uppercase tracking-wider bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded">{d.phone_type}</span>}
            {locked.has("phone") && <Lock className="h-3 w-3 text-emerald-600" data-testid="caller-detail-phone-lock" />}
          </span>
          <span className="font-semibold text-flag-navy">{d.phone}</span>
        </a>
      )}
      {d.phone2 && (
        <a href={`tel:${d.phone2.replace(/[^\d+]/g, "")}`}
           onClick={() => recordChannel("call")}
           className="flex items-center justify-between py-1.5 text-base hover:text-flag-red border-t border-slate-100"
           data-testid="caller-detail-phone2">
          <span className="inline-flex items-center gap-2 text-slate-600 text-sm">
            <Phone className="h-4 w-4" />
            Phone 2
            {d.phone2_type && <span className="text-xs font-bold uppercase tracking-wider bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded">{d.phone2_type}</span>}
            {locked.has("phone2") && <Lock className="h-3 w-3 text-emerald-600" />}
          </span>
          <span className="font-semibold text-flag-navy">{d.phone2}</span>
        </a>
      )}
      {d.email && (
        <a href={`mailto:${d.email}`}
           onClick={() => recordChannel("email")}
           className={`flex items-center justify-between py-1.5 text-base hover:text-flag-red ${(d.phone || d.phone2) ? "border-t border-slate-100" : ""}`}
           data-testid="caller-detail-email">
          <span className="inline-flex items-center gap-2 text-slate-600 text-sm">
            <Mail className="h-4 w-4" /> Email
            {locked.has("email") && <Lock className="h-3 w-3 text-emerald-600" data-testid="caller-detail-email-lock" />}
          </span>
          <span className="font-semibold text-flag-navy truncate max-w-[65%]">{d.email}</span>
        </a>
      )}
      {!hasAny && (
        <div className="flex flex-wrap gap-2 py-1">
          <button type="button" onClick={openEdit}
                  className="inline-flex items-center gap-1 text-sm font-semibold text-flag-navy border border-slate-300 rounded-md px-3 py-1.5 hover:bg-slate-50"
                  data-testid="caller-detail-add-phone-btn">
            <Plus className="h-3.5 w-3.5" /> Add phone
          </button>
          <button type="button" onClick={openEdit}
                  className="inline-flex items-center gap-1 text-sm font-semibold text-flag-navy border border-slate-300 rounded-md px-3 py-1.5 hover:bg-slate-50"
                  data-testid="caller-detail-add-email-btn">
            <Plus className="h-3.5 w-3.5" /> Add email
          </button>
        </div>
      )}
    </div>
  );
}


function DonorDetail({ donor, fieldAvailability, position, total, hasPrev, hasNext, onPrev, onNext, onClose, onStatusSaved, categoryType = "call_list", categoryId = null, layout = "modal", onMovedToMaxed = null, campaignConfig = null }) {
  const donorId = donor.id;
  const isProspecting = categoryType === "prospecting_list";
  const isPac = categoryType === "pac_list";
  // State-matching mode is a NEW branch introduced in Step 3. It replaces
  // the federal ask buttons and the "Can still give … for the general
  // election" capacity box with matching-funds equivalents. The gate
  // stays a pure boolean so the ENTIRE federal render path below is
  // literally byte-identical when this evaluates to false — which it
  // does for every one of the 14 existing caller accounts today.
  const isStateMatching = (campaignConfig?.campaign_type === "state_matching");
  // Prospecting and PAC lists don't use matching-funds semantics either
  // (PAC has no ceiling; Prospecting has no giving history), so treat
  // state_matching as inert on those two lists.
  const smActive = isStateMatching && !isProspecting && !isPac;
  const matchableCeiling = Number(campaignConfig?.matchable_ceiling ?? 250) || 0;
  // Both PAC and Prospecting hide the "Can still give" capacity box and
  // the maxed badge / move-to-maxed button. The reasons differ (PAC has
  // no cap; Prospecting has no giving history), but the rendered layout
  // is the same for those two elements.
  const hideCap = isProspecting || isPac;
  const isInline = layout === "inline";
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [saving, setSaving] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [copyState, setCopyState] = useState("idle");
  const [askMode, setAskMode] = useState(categoryType === "pac_list" ? "highest" : "max");  // "max" | "highest"
  // State-matching ask picker. Keyed separately from the federal `askMode`
  // so flipping between the two doesn't drop the federal caller's choice
  // (defensive — accounts don't toggle mid-session, but this keeps the
  // branches fully independent).
  //   "maximize" (default) — matchable_ceiling - cycle_giving, floor 0
  //   "previous"           — donor.last_gift_amount
  //   "other"              — caller-typed manual amount
  const [smAskMode, setSmAskMode] = useState("maximize");
  const [smOtherAmount, setSmOtherAmount] = useState("");
  // PAC lists let the caller override the ask with an arbitrary dollar
  // amount (no cap applies). Empty string means "use the button's
  // suggested amount"; a positive number replaces the currency figure in
  // the rendered message.
  const [manualAsk, setManualAsk] = useState("");
  const [showFullHistory, setShowFullHistory] = useState(false);
  // Message editor is collapsed by default so the call-moment actions
  // (Call / outcomes) don't get pushed offscreen on mobile. Most callers
  // never edit the message mid-call — they just tap Call.
  const [expandMessage, setExpandMessage] = useState(false);
  // Mobile-only bottom sheet that surfaces the 5 outcome buttons from the
  // fixed bottom bar. Desktop shows outcomes inline so this stays false.
  const [showOutcomeSheet, setShowOutcomeSheet] = useState(false);
  // Two extra bottom sheets tied to specific outcomes. Neither slows the
  // happy path — both offer a Skip that logs the outcome without extras.
  const [pledgePrompt, setPledgePrompt] = useState(false);
  const [followUpPrompt, setFollowUpPrompt] = useState(false);
  // Reference & history block — committees / work / giving history — is
  // collapsed by default. On the call moment the caller almost never needs
  // this info, and hiding it keeps outcomes + notes reachable without
  // extra scroll. Prospecting lists don't have any of these sections to
  // begin with, so the toggle is auto-hidden there.
  const [showReference, setShowReference] = useState(false);
  // Which outreach channel the caller most recently tapped (Call / Text /
  // Email) — captured as an attribute on the NEXT outcome logged. Cleared
  // when the donor changes so it can't leak across donors.
  const channelInitiatedRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setErr("");
    setNoteText("");
    setCopyState("idle");
    setAskMode("max");
    setShowFullHistory(false);
    setExpandMessage(false);
    setShowOutcomeSheet(false);
    setPledgePrompt(false);
    setFollowUpPrompt(false);
    setShowReference(false);
    channelInitiatedRef.current = null;

    // Feb 2026 latency fix: the list response now bundles per-donor
    // messages_max / messages_highest / category_type, so a donor selection
    // is a pure state change — no network round-trip. We build the same
    // `data` shape the /caller/donors/{id} endpoint used to return and set
    // it synchronously. If (for any reason — legacy list payloads, deep
    // links) the messages aren't attached to the donor prop, we fall back
    // to the detail endpoint so nothing breaks.
    const preRendered = donor && donor.messages_max
      ? {
          donor,
          messages: donor.messages_max,
          ask_mode: "max",
          category_type: donor.category_type || categoryType || "call_list",
        }
      : null;
    if (preRendered) {
      setData(preRendered);
      setMsg(preRendered.messages?.text || "");
      return () => { cancelled = true; };
    }
    setData(null);
    api.get(`/caller/donors/${donorId}?ask_mode=max${categoryId ? `&category_id=${categoryId}` : ""}`)
      .then(({ data }) => {
        if (cancelled) return;
        setData(data);
        setMsg(data.messages?.text || "");
      })
      .catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message));
    return () => { cancelled = true; };
  }, [donorId, donor, categoryType, categoryId]);

  const setStatus = async (status, extras = {}) => {
    if (saving) return;
    setSaving(true);
    try {
      const payload = { status };
      // Include the current draft-note text (if any) so it lands on the log
      // entry alongside the outcome — same behavior as the previous
      // status_note field but now also written to caller_call_log.
      if (noteText.trim()) payload.note = noteText.trim();
      if (channelInitiatedRef.current) payload.channel_initiated = channelInitiatedRef.current;
      // Outcome-specific extras from the pledge / follow-up bottom sheets.
      // The server ignores fields that don't apply to the given status.
      if (extras.pledge_amount != null && Number.isFinite(Number(extras.pledge_amount))) {
        payload.pledge_amount = Number(extras.pledge_amount);
      }
      if (extras.follow_up_date) payload.follow_up_date = extras.follow_up_date;
      await api.patch(`/caller/donors/${donorId}/status`, payload);
      toast.success(`Marked ${STATUS_LABELS[status]?.label || status}`);
      setShowOutcomeSheet(false);
      setPledgePrompt(false);
      setFollowUpPrompt(false);
      onStatusSaved && onStatusSaved({
        outcome: status,
        pledge_amount: payload.pledge_amount,
        follow_up_date: payload.follow_up_date,
      });
      // Advance to the next donor rather than closing the detail; if we're
      // at the end of the list, close the overlay to return to the list.
      if (hasNext) {
        onNext();
      } else {
        onClose();
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  // Outcome gate: happy path is one tap → advance. Pledged and Follow-up
  // insert a single confirmation sheet (both include Skip so the caller
  // can preserve one-tap behavior when they don't have the number/date).
  const pickOutcome = (outcome) => {
    if (outcome === "pledged") {
      setShowOutcomeSheet(false);
      setPledgePrompt(true);
      return;
    }
    if (outcome === "call_back") {
      setShowOutcomeSheet(false);
      setFollowUpPrompt(true);
      return;
    }
    setStatus(outcome);
  };

  // Called from the Call / Text / Email link's onClick. Doesn't block the
  // browser handoff — just records which channel the caller initiated so
  // the next outcome log carries it as an attribute.
  const recordChannel = (channel) => {
    channelInitiatedRef.current = channel;
  };

  const noteInputRef = useRef(null);
  // Manual per-list MOVE (distinct from automatic additive maxing): removes
  // the donor from THIS list only, adds Maxed Donors. Other lists + All
  // Donors + history untouched.
  const [movingToMaxed, setMovingToMaxed] = useState(false);
  const moveToMaxed = async () => {
    if (!categoryId || movingToMaxed || !d) return;
    if (!window.confirm(
      `Move ${d.first_name} ${d.last_name} to Maxed Donors?\n\nThey'll be removed from this list only — they stay on any other lists and in All Donors, and all notes and call history are kept.`
    )) return;
    setMovingToMaxed(true);
    try {
      const { data: res } = await api.post(`/caller/donors/${donorId}/move-to-maxed`, { from_category_id: categoryId });
      toast.success(`Moved to ${res.moved_to?.name || "Maxed Donors"} — removed from ${res.removed_from?.name || "this list"}`);
      if (onMovedToMaxed) onMovedToMaxed(); else onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setMovingToMaxed(false);
    }
  };

  const addNote = async () => {
    const trimmed = noteText.trim();
    if (!trimmed || savingNote) return;
    setSavingNote(true);
    try {
      const { data: note } = await api.post(`/caller/donors/${donorId}/notes`, { text: trimmed });
      setData((prev) => prev
        ? { ...prev, donor: { ...prev.donor, notes: [...(prev.donor.notes || []), note] } }
        : prev);
      setNoteText("");
      // Iter 41 spec: after saving, a fresh empty note box opens
      // immediately so the caller can add another without extra taps.
      requestAnimationFrame(() => {
        try { noteInputRef.current?.focus(); } catch { /* noop */ }
      });
      toast.success("Note saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSavingNote(false);
    }
  };

  const swapAskAmount = async (newMode) => {
    // Ask-mode toggle without a network hop: the list already delivered
    // both messages_max and messages_highest per donor. If for any reason
    // those aren't attached (legacy payload, direct URL, fallback fetch
    // path), fall back to the detail endpoint with the requested mode.
    if (newMode === askMode) return;
    if (!data) return;
    // Only clobber the message textarea if the caller hasn't started
    // editing — mid-edit swaps preserve her in-progress text.
    const untouched = msg === (data.messages?.text || "");
    setAskMode(newMode);
    const preRendered = newMode === "max" ? donor?.messages_max : donor?.messages_highest;
    if (preRendered) {
      const fresh = { ...data, messages: preRendered, ask_mode: newMode };
      setData(fresh);
      if (untouched) setMsg(preRendered.text || "");
      return;
    }
    try {
      const { data: fresh } = await api.get(`/caller/donors/${donorId}?ask_mode=${newMode}${categoryId ? `&category_id=${categoryId}` : ""}`);
      setData(fresh);
      if (untouched) {
        setMsg(fresh.messages?.text || "");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const copyMessage = async () => {
    // Text/Email/Copy all read from the same `msg` state so they can't drift.
    // On PAC lists the caller's manual-ask override is applied here too.
    // On state-matching accounts the state-matching amount override is
    // applied instead.
    const toCopy = smActive ? _applyStateMatchingAsk(msg) : _applyManualAsk(msg);
    // Try navigator.clipboard first; if it rejects (denied / non-secure /
    // programmatic-copy blocked), fall back to the select-and-execCommand
    // path before giving up.
    const fallbackCopy = () => {      const ta = document.createElement("textarea");
      ta.value = toCopy;
      ta.setAttribute("readonly", "");
      ta.style.position = "fixed";
      ta.style.top = "-9999px";
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      if (!ok) throw new Error("execCommand copy returned false");
    };
    try {
      if (navigator.clipboard && window.isSecureContext) {
        try {
          await navigator.clipboard.writeText(toCopy);
        } catch {
          fallbackCopy();
        }
      } else {
        fallbackCopy();
      }
      setCopyState("copied");
      toast.success("Message copied");
      setTimeout(() => setCopyState("idle"), 2000);
    } catch {
      setCopyState("error");
      toast.error("Copy failed — select the message text and copy manually");
    }
  };

  const d = data?.donor;
  const messages = data?.messages;
  const notes = useMemo(() => d?.notes || [], [d]);
  // Newest-first, computed once per data change instead of on every render.
  const sortedNotes = useMemo(
    () => [...notes].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")),
    [notes]
  );

  const telHref = d?.phone ? `tel:${d.phone.replace(/[^\d+]/g, "")}` : "";
  // On PAC lists, `manualAsk` (when > 0) replaces the ask dollar figure
  // in the pre-rendered message. The backend renders PAC messages using
  // highest_gift, so we swap that specific dollar string for the manual
  // amount client-side — same result as a re-render, no round-trip.
  const _applyManualAsk = (s) => {
    if (!isPac || !s) return s || "";
    const n = Number(manualAsk);
    if (!(n > 0)) return s;
    const highestStr = d?.highest_gift > 0
      ? `$${Math.floor(d.highest_gift).toLocaleString("en-US")}`
      : null;
    const manualStr = `$${Math.floor(n).toLocaleString("en-US")}`;
    return highestStr ? s.split(highestStr).join(manualStr) : s;
  };

  // -------- State-matching ask math (whole-cycle ceiling) --------
  // Ceiling applies to the donor's TOTAL cycle giving (primary + general
  // COMBINED), NOT per-election. This is a single cycle-wide bucket:
  //   matchable_room = max(0, matchable_ceiling - (primary_2026 + general_2026))
  // current_covered_election is deliberately NOT consulted for math — the
  // math is whole-cycle. The field remains available for future message
  // wording only.
  const smCycleGiving = (Number(d?.primary_2026) || 0) + (Number(d?.general_2026) || 0);
  const smMatchableRoom = Math.max(0, Math.floor(matchableCeiling - smCycleGiving));
  const smPreviousAmount = Math.max(0, Math.floor(Number(d?.last_gift_amount) || 0));
  const smOtherNum = (() => {
    const n = Number(smOtherAmount);
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
  })();
  const smSelectedAmount = smAskMode === "previous"
    ? smPreviousAmount
    : smAskMode === "maximize"
      ? smMatchableRoom
      : smOtherNum;

  // Swap the FIRST currency token in a pre-rendered federal message with
  // the state-matching ask amount, AND replace the federal-election trail
  // ("for the general election") with matching-funds wording so the copy
  // reads naturally for a state race with a matchable cap. Federal
  // renders never call this — federal accounts see the original message
  // verbatim.
  const _applyStateMatchingAsk = (s) => {
    if (!smActive || !s) return s || "";
    const askStr = `$${smSelectedAmount.toLocaleString("en-US")}`;
    let out = /\$[\d,]+/.test(s) ? s.replace(/\$[\d,]+/, askStr) : s;
    const ceilingStr = `$${matchableCeiling.toLocaleString("en-US")}`;
    // Replace the federal trailing phrase everywhere it appears (some
    // templates put it in the subject/body too). The match is case-
    // insensitive but preserves the sentence around it.
    out = out.replace(
      /for the general election/gi,
      `toward this cycle's ${ceilingStr} matchable max`,
    );
    return out;
  };

  // Chose the correct message-transform based on the account mode. Federal
  // and PAC paths are unchanged (`_applyManualAsk` behaves as before);
  // only state-matching accounts see `_applyStateMatchingAsk` intercept.
  const _applyAskAmount = smActive ? _applyStateMatchingAsk : _applyManualAsk;
  const shownMsg = _applyAskAmount(msg);
  const shownSubject = _applyAskAmount(messages?.email_subject || "");
  const shownBody = _applyAskAmount(messages?.email_body || "");
  const smsHref = d?.phone
    ? `sms:${d.phone.replace(/[^\d+]/g, "")}?&body=${encodeURIComponent(shownMsg)}`
    : "";
  const mailtoHref = d?.email
    ? `mailto:${d.email}?subject=${encodeURIComponent(shownSubject)}&body=${encodeURIComponent(shownBody)}`
    : "";

  const lastGiftText = d && (d.last_gift_amount > 0 || d.last_gift_date)
    ? `${fmtMoney(d.last_gift_amount)} · ${fmtDate(d.last_gift_date)}`
    : "—";

  return (
    <div
      className={isInline ? "relative bg-slate-50" : "fixed inset-0 z-30 bg-slate-50 overflow-y-auto"}
      data-testid={`caller-detail-${donorId}`}
      role={isInline ? undefined : "dialog"}
    >
      <header className={isInline ? "sticky top-0 z-10 bg-white border-b border-slate-200" : "sticky top-0 z-10 bg-white border-b border-slate-200"}>
        <div className={isInline ? "px-4 py-3 flex items-center justify-between gap-2" : "max-w-md mx-auto px-4 py-3 flex items-center justify-between gap-2"}>
          {isInline ? (
            <div className="text-sm font-semibold text-slate-500">Donor detail</div>
          ) : (
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center gap-1 text-sm font-semibold text-slate-600 hover:text-flag-navy"
              data-testid="caller-detail-back"
            >
              <ArrowLeft className="h-4 w-4" /> Back to list
            </button>
          )}
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500" data-testid="caller-detail-position">
            Donor {position} of {total}
          </div>
        </div>
        <div className={isInline ? "px-4 pb-2 flex items-center gap-2" : "max-w-md mx-auto px-4 pb-2 flex items-center gap-2"}>
          <Button
            onClick={onPrev}
            disabled={!hasPrev}
            variant="outline"
            className="flex-1 h-9 border-2 font-semibold disabled:opacity-40"
            data-testid="caller-detail-prev"
          >
            <ChevronLeft className="h-4 w-4 mr-1" /> Previous
          </Button>
          <Button
            onClick={onNext}
            disabled={!hasNext}
            variant="outline"
            className="flex-1 h-9 border-2 font-semibold disabled:opacity-40"
            data-testid="caller-detail-next"
          >
            Next <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </header>

      <main className={isInline ? "px-4 py-4 space-y-4 pb-8" : "max-w-md sm:max-w-lg mx-auto px-4 py-4 space-y-4 pb-32"}>
        {err && <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg px-3 py-2 text-base">{err}</div>}
        {!data && !err && <div className="text-center text-slate-500 py-10 text-base">Loading donor&hellip;</div>}
        {d && (
          <>
            {/* 1. Name + capacity (or city/state for prospecting) — kept
                at top but tight so actions are reachable without scroll. */}
            <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5 shadow-sm">
              <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Donor</div>
              <div className="font-display text-2xl sm:text-3xl font-black text-flag-navy" data-testid="caller-detail-name">
                {d.first_name} {d.last_name}
                {d.maxed && !isPac && !isProspecting && (
                  <span
                    className="ml-2 align-middle text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-flag-red text-white"
                    data-testid="caller-detail-maxed-badge"
                  >
                    Maxed
                  </span>
                )}
                {isProspecting && d.total_given > 0 && (
                  <span
                    className="ml-2 align-middle text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-emerald-600 text-white whitespace-nowrap"
                    data-testid="caller-detail-already-donating-badge"
                  >
                    Already Donating
                  </span>
                )}
              </div>
              {(d.city || d.state) && (
                <div
                  className="text-sm text-slate-600 mt-1"
                  data-testid={isProspecting ? "caller-detail-town-state" : "caller-detail-city"}
                >
                  {[d.city, d.state].filter(Boolean).join(", ")}{d.zip ? ` ${d.zip}` : ""}
                </div>
              )}
              {!hideCap && !smActive && (
                <div className="mt-3 bg-flag-red/5 border-2 border-flag-red rounded-lg px-4 py-2.5" data-testid="caller-detail-capacity-box">
                  <div className="text-xs font-bold uppercase tracking-wider text-flag-red flex items-center gap-1.5">
                    <DollarSign className="h-4 w-4" /> Can still give
                  </div>
                  <div className="font-display text-3xl sm:text-4xl font-black text-flag-red mt-0.5" data-testid="caller-detail-room">
                    {fmtMoney(d.general_room)}
                  </div>
                  <div className="text-sm text-slate-700">for the general election</div>
                </div>
              )}
              {/* State-matching capacity box — swaps the federal "Can still
                  give … for the general election" language for a simple
                  matchable-room readout. The ceiling applies to primary +
                  general COMBINED (whole-cycle bucket), not per-election. */}
              {!hideCap && smActive && (
                <div
                  className="mt-3 bg-flag-navy/5 border-2 border-flag-navy rounded-lg px-4 py-2.5"
                  data-testid="caller-detail-matchable-room-box"
                >
                  <div className="text-xs font-bold uppercase tracking-wider text-flag-navy flex items-center gap-1.5">
                    <DollarSign className="h-4 w-4" /> Matchable room
                  </div>
                  <div
                    className="font-display text-3xl sm:text-4xl font-black text-flag-navy mt-0.5"
                    data-testid="caller-detail-matchable-room"
                  >
                    {fmtMoney(smMatchableRoom)}
                  </div>
                  <div className="text-sm text-slate-700">
                    of {fmtMoney(matchableCeiling)} cycle ceiling
                    {smCycleGiving > 0 && (
                      <span className="text-slate-500"> · already gave {fmtMoney(smCycleGiving)}</span>
                    )}
                  </div>
                </div>
              )}
              {/* One-click MOVE to Maxed Donors — only offered when the
                  donor has actually hit the limit and we're viewing them
                  from a specific Call List (never from the Maxed tab
                  itself, and never from a PAC list — no cap applies). */}
              {d.maxed && categoryId && categoryType !== "maxed" && !isPac && (
                <button
                  type="button"
                  onClick={moveToMaxed}
                  disabled={movingToMaxed}
                  data-testid="caller-move-to-maxed-btn"
                  className="mt-3 w-full min-h-[44px] rounded-lg border-2 border-flag-red bg-white text-flag-red font-bold text-sm hover:bg-flag-red hover:text-white transition-colors disabled:opacity-50"
                >
                  {movingToMaxed ? "Moving…" : "Move to Maxed Donors (remove from this list)"}
                </button>
              )}
            </div>

            {/* Read-only "Already Donating" giving summary — appears on
                Prospecting cards ONLY when the canonical donor has actual
                giving history (total_given > 0). Additive to the
                Prospecting card; never affects Call / PAC / Maxed views
                and never enables the capacity box, ask buttons, or
                donation-link tokens on Prospecting messages. */}
            {isProspecting && d.total_given > 0 && (
              <div
                className="bg-emerald-50 border-2 border-emerald-600 rounded-xl p-4 shadow-sm"
                data-testid="caller-detail-already-donating-summary"
              >
                <div className="text-xs font-bold uppercase tracking-wider text-emerald-700 mb-2">
                  Giving history on file
                </div>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
                  {(d.last_gift_amount > 0 || d.last_gift_date) && (
                    <div className="flex items-baseline justify-between gap-2 sm:col-span-2">
                      <dt className="text-slate-600">Last gift</dt>
                      <dd className="font-semibold text-emerald-800 tabular-nums text-right"
                          data-testid="already-donating-last-gift">
                        {[d.last_gift_amount > 0 ? fmtMoney(d.last_gift_amount) : null,
                          d.last_gift_date ? fmtDate(d.last_gift_date) : null]
                          .filter(Boolean).join(" · ")}
                      </dd>
                    </div>
                  )}
                  {d.highest_gift > 0 && (
                    <div className="flex items-baseline justify-between gap-2">
                      <dt className="text-slate-600">Highest gift</dt>
                      <dd className="font-semibold text-emerald-800 tabular-nums text-right"
                          data-testid="already-donating-highest-gift">
                        {fmtMoney(d.highest_gift)}
                      </dd>
                    </div>
                  )}
                  <div className="flex items-baseline justify-between gap-2">
                    <dt className="text-slate-600">Total given</dt>
                    <dd className="font-semibold text-emerald-800 tabular-nums text-right"
                        data-testid="already-donating-total-given">
                      {fmtMoney(d.total_given)}
                    </dd>
                  </div>
                  {d.number_of_gifts > 0 && (
                    <div className="flex items-baseline justify-between gap-2">
                      <dt className="text-slate-600">Number of gifts</dt>
                      <dd className="font-semibold text-emerald-800 tabular-nums text-right"
                          data-testid="already-donating-number-of-gifts">
                        {d.number_of_gifts}
                      </dd>
                    </div>
                  )}
                </dl>
              </div>
            )}

            {/* 2a. Contact — compact so it doesn't push actions down. Shows
                which phone/email exist and lets the caller tap them
                directly as an alternative to the big Call button below.
                Caller can EDIT any field via the pencil; edited fields
                become protected from all future upload overwrites. */}
            <ContactBlock
              donor={d}
              onSaved={(fresh) => setData((prev) => (
                prev ? { ...prev, donor: { ...prev.donor, ...fresh } } : prev
              ))}
              recordChannel={recordChannel}
            />

            {/* 2b. PRIMARY action — Call. Full-width and visually dominant
                because it's the action a caller performs 50 times a
                session. Text/Email/Copy sit below as a secondary row. */}
            <div className="space-y-2">
              <PrimaryCallButton
                href={telHref}
                disabled={!d.phone}
                onClick={() => recordChannel("call")}
              />
              <div className="grid grid-cols-3 gap-2">
                <ActionButton href={smsHref} disabled={!d.phone} icon={MessageSquare} label="Text" testid="caller-action-text" onClick={() => recordChannel("text")} secondary />
                <ActionButton href={mailtoHref} disabled={!d.email} icon={Mail} label="Email" testid="caller-action-email" onClick={() => recordChannel("email")} secondary />
                <button
                  type="button"
                  onClick={copyMessage}
                  data-testid="caller-action-copy"
                  className={`inline-flex flex-col items-center justify-center gap-1 min-h-[52px] rounded-xl text-sm font-bold shadow-sm border-2 transition-colors ${
                    copyState === "copied"
                      ? "bg-emerald-600 border-emerald-600 text-white"
                      : "bg-white border-flag-navy text-flag-navy hover:bg-flag-navy hover:text-white"
                  }`}
                >
                  <CopyIcon className="h-5 w-5" /> {copyState === "copied" ? "Copied" : "Copy"}
                </button>
              </div>
            </div>

            {/* 3. Ask-amount toggle — swaps the dollar figure inside the
                message. Hidden entirely for prospecting-list categories.
                On PAC lists the "Ask for max" button is hidden (no cap
                applies); "Ask for their highest amount" is the primary
                path, with a manual override below.
                State-matching accounts skip this entirely and render the
                three-button matching-funds picker just below instead. */}
            {!isProspecting && !smActive && (
              <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm" data-testid="ask-amount-toggle">
                <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">What are you asking for?</div>
                <div className={`grid ${isPac ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-2"} gap-2`}>
                  {!isPac && (
                    <button
                      type="button"
                      onClick={() => swapAskAmount("max")}
                      data-testid="ask-btn-max"
                      aria-pressed={askMode === "max"}
                      className={`min-h-[52px] rounded-lg border-2 px-3 py-2 text-base font-bold transition-colors ${
                        askMode === "max"
                          ? "bg-flag-red border-flag-red text-white shadow"
                          : "bg-white border-slate-300 text-flag-navy hover:border-flag-red"
                      }`}
                    >
                      Ask for max
                      <div className={`text-sm font-semibold mt-0.5 ${askMode === "max" ? "text-white/90" : "text-slate-500"}`}>
                        {fmtMoney(d.general_room)}
                      </div>
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => swapAskAmount("highest")}
                    data-testid="ask-btn-highest"
                    aria-pressed={askMode === "highest"}
                    disabled={!(d.highest_gift > 0)}
                    className={`min-h-[52px] rounded-lg border-2 px-3 py-2 text-base font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                      askMode === "highest"
                        ? "bg-flag-navy border-flag-navy text-white shadow"
                        : "bg-white border-slate-300 text-flag-navy hover:border-flag-navy"
                    }`}
                  >
                    {isPac ? "Ask their highest gift" : "Ask for their highest amount"}
                    <div className={`text-sm font-semibold mt-0.5 ${askMode === "highest" ? "text-white/90" : "text-slate-500"}`}>
                      {d.highest_gift > 0 ? fmtMoney(d.highest_gift) : "no data"}
                    </div>
                  </button>
                </div>
                {isPac && (
                  <div className="mt-3 flex items-center gap-2" data-testid="ask-manual-row">
                    <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500 whitespace-nowrap">
                      Manual ask $
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={manualAsk}
                      onChange={(e) => setManualAsk(e.target.value)}
                      placeholder="e.g. 2500"
                      className="h-9 flex-1 min-w-0 rounded-md border border-slate-300 px-2 text-sm"
                      data-testid="ask-manual-input"
                    />
                    <span className="text-[10.5px] text-slate-400">no cap on PAC</span>
                  </div>
                )}
              </div>
            )}

            {/* 3b. State-matching ask picker — replaces the federal buttons
                above when the caller's account is in matching-funds mode.
                Three big, phone-friendly buttons keyed to whole-cycle
                math (primary + general combined against a single ceiling):
                  Previous amount — last_gift_amount
                  Maximize Match — max(0, ceiling − cycle_giving)
                  Other          — caller-typed manual amount
                Federal path is completely untouched; this block never
                renders on a federal account. */}
            {smActive && (
              <div
                className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm"
                data-testid="sm-ask-picker"
              >
                <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
                  What are you asking for?
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <button
                    type="button"
                    onClick={() => setSmAskMode("previous")}
                    aria-pressed={smAskMode === "previous"}
                    disabled={!(smPreviousAmount > 0)}
                    data-testid="sm-ask-btn-previous"
                    className={`min-h-[64px] rounded-lg border-2 px-3 py-2 text-base font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                      smAskMode === "previous"
                        ? "bg-flag-navy border-flag-navy text-white shadow"
                        : "bg-white border-slate-300 text-flag-navy hover:border-flag-navy"
                    }`}
                  >
                    Previous amount
                    <div
                      className={`text-sm font-semibold mt-0.5 ${smAskMode === "previous" ? "text-white/90" : "text-slate-500"}`}
                      data-testid="sm-ask-btn-previous-amount"
                    >
                      {smPreviousAmount > 0 ? fmtMoney(smPreviousAmount) : "no prior gift"}
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setSmAskMode("maximize")}
                    aria-pressed={smAskMode === "maximize"}
                    disabled={!(smMatchableRoom > 0)}
                    data-testid="sm-ask-btn-maximize"
                    className={`min-h-[64px] rounded-lg border-2 px-3 py-2 text-base font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                      smAskMode === "maximize"
                        ? "bg-flag-red border-flag-red text-white shadow"
                        : "bg-white border-slate-300 text-flag-navy hover:border-flag-red"
                    }`}
                  >
                    Maximize Match
                    <div
                      className={`text-sm font-semibold mt-0.5 ${smAskMode === "maximize" ? "text-white/90" : "text-slate-500"}`}
                      data-testid="sm-ask-btn-maximize-amount"
                    >
                      {fmtMoney(smMatchableRoom)}
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setSmAskMode("other")}
                    aria-pressed={smAskMode === "other"}
                    data-testid="sm-ask-btn-other"
                    className={`min-h-[64px] rounded-lg border-2 px-3 py-2 text-base font-bold transition-colors ${
                      smAskMode === "other"
                        ? "bg-flag-navy border-flag-navy text-white shadow"
                        : "bg-white border-slate-300 text-flag-navy hover:border-flag-navy"
                    }`}
                  >
                    Other
                    <div className={`text-sm font-semibold mt-0.5 ${smAskMode === "other" ? "text-white/90" : "text-slate-500"}`}>
                      manual
                    </div>
                  </button>
                </div>
                {smAskMode === "other" && (
                  <div className="mt-3 flex items-center gap-2" data-testid="sm-ask-other-row">
                    <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500 whitespace-nowrap">
                      Other ask $
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={smOtherAmount}
                      onChange={(e) => setSmOtherAmount(e.target.value)}
                      placeholder="e.g. 100"
                      className="h-10 flex-1 min-w-0 rounded-md border border-slate-300 px-2 text-base"
                      data-testid="sm-ask-other-input"
                    />
                  </div>
                )}
              </div>
            )}

            {/* 4. Message editor — collapsed to a 3-line preview by
                default. Most callers never edit mid-call; those who do
                tap Expand to reveal the full editor. Same state either
                way; no data is lost by collapsing. */}
            <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
              <div className="flex items-center justify-between mb-1.5">
                <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Message</div>
                <button
                  type="button"
                  onClick={() => setExpandMessage((v) => !v)}
                  className="inline-flex items-center gap-1 text-xs font-bold text-flag-navy hover:text-flag-red"
                  data-testid="caller-message-toggle"
                  aria-expanded={expandMessage}
                >
                  {expandMessage ? (<><ChevronUp className="h-4 w-4" /> Collapse</>) : (<><ChevronDown className="h-4 w-4" /> Expand to edit</>)}
                </button>
              </div>
              {expandMessage ? (
                <Textarea
                  value={shownMsg}
                  onChange={(e) => setMsg(e.target.value)}
                  rows={10}
                  className="text-base leading-relaxed"
                  data-testid="caller-detail-message"
                />
              ) : (
                <button
                  type="button"
                  onClick={() => setExpandMessage(true)}
                  className="w-full text-left text-sm leading-relaxed text-slate-700 bg-slate-50 border border-slate-200 rounded-md px-3 py-2 line-clamp-3 hover:border-flag-navy"
                  data-testid="caller-detail-message-preview"
                  aria-label="Expand message to edit"
                >
                  {shownMsg || "No message drafted"}
                </button>
              )}
            </div>

            {/* 5. Outcome logging — always visible inline (desktop needs
                this; mobile also gets it via the sticky bottom-sheet).
                Larger 56px tap targets and consistent color coding for
                65+ users on a phone. */}
            <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm" data-testid="caller-outcome-panel">
              <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Log outcome (then advances to next)</div>
              <OutcomeGrid onPick={pickOutcome} />
            </div>

            {/* 6. Notes / call log — chronological history stays high on
                the page so callers can scan prior context before dialing
                and log new notes right after logging outcome. */}
            <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm" data-testid="caller-detail-notes">
              <div className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5 mb-2">
                <StickyNote className="h-4 w-4" /> Call notes ({notes.length})
              </div>
              {notes.length > 0 && (
                <ul className="space-y-2 mb-3 max-h-64 overflow-y-auto pr-1">
                  {sortedNotes
                    .map((n) => {
                      const source = n.source || "note";
                      const isImported = source === "imported";
                      const isOutcome = source === "outcome";
                      const borderCls = isImported
                        ? "border-amber-400 bg-amber-50/40"
                        : isOutcome
                          ? "border-flag-red bg-flag-red/5"
                          : "border-slate-200";
                      return (
                        <li
                          key={n.id}
                          className={`border-l-2 pl-2.5 py-1 ${borderCls}`}
                          data-testid={`caller-note-${n.id}`}
                          data-source={source}
                        >
                          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 flex flex-wrap items-center gap-1.5">
                            {isImported ? (
                              <span className="text-amber-800 font-bold">Imported · Background</span>
                            ) : (
                              <span>{fmtDateTime(n.created_at)}</span>
                            )}
                            {isOutcome && (
                              <>
                                <span className="text-flag-red font-bold">
                                  {(OUTCOME_FILTER_OPTIONS.find((o) => o.value === n.outcome) || {}).label || n.outcome}
                                </span>
                                {n.pledge_amount != null && (
                                  <span className="text-emerald-700 font-bold" data-testid={`note-pledge-${n.id}`}>
                                    · ${Math.round(Number(n.pledge_amount)).toLocaleString()}
                                  </span>
                                )}
                                {n.follow_up_date && (
                                  <span className="text-sky-700 font-bold" data-testid={`note-followup-${n.id}`}>
                                    · Due {n.follow_up_date}
                                  </span>
                                )}
                                {n.channel_initiated && (
                                  <span className="text-slate-500">
                                    · {CHANNEL_LABEL[n.channel_initiated] || n.channel_initiated}
                                  </span>
                                )}
                              </>
                            )}
                          </div>
                          {n.text && (
                            <div className="text-base text-slate-800 whitespace-pre-wrap">{n.text}</div>
                          )}
                        </li>
                      );
                    })}
                </ul>
              )}
              <Textarea
                ref={noteInputRef}
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                rows={2}
                placeholder='e.g. "Spoke to wife, call back after 6" · "Pledged $1k for the general"'
                className="text-sm"
                data-testid="caller-detail-note-input"
              />
              <Button
                onClick={addNote}
                disabled={!noteText.trim() || savingNote}
                className="mt-2 h-10 w-full bg-flag-navy hover:bg-flag-navy/90 text-white font-bold"
                data-testid="caller-detail-note-save"
              >
                <Plus className="h-4 w-4 mr-1" /> {savingNote ? "Saving…" : "Add note"}
              </Button>
            </div>

            {/* -------- Reference info (collapsed by default) --------
                On the call moment Claudia almost never needs committees,
                employer, or giving-history breakdowns. Hiding them by
                default keeps the outcomes + notes reachable without a
                scroll war; a single tap expands them when she wants
                context. Prospecting lists don't have any of these
                sections, so the toggle auto-hides in that case. */}
            {!isProspecting && (
              d.committee_giving?.length > 0
              || d.employer || d.occupation
              || d.giving_history || d.gifts
              // Direct giving fields — a donor may have these without
              // committee_giving / employer / a legacy `giving_history` map.
              // Without checking them, House File donors like Patricia Martin
              // (who has last_gift, highest_gift, total_given, etc. but none
              // of the legacy fields) never see the toggle and their history
              // stays hidden. See bug report Feb 2026.
              || d.last_gift_amount > 0 || d.last_gift_date
              || d.highest_gift > 0 || d.total_given > 0
              || d.number_of_gifts > 0 || d.first_gift_date
              || d.primary_2026 > 0 || d.general_2026 > 0
            ) && (
              <button
                type="button"
                onClick={() => setShowReference((v) => !v)}
                data-testid="caller-reference-toggle"
                aria-expanded={showReference}
                className="w-full inline-flex items-center justify-between px-4 py-3 rounded-xl bg-white border border-slate-200 shadow-sm text-sm font-bold text-flag-navy hover:border-flag-navy"
              >
                <span className="inline-flex items-center gap-2">
                  <StickyNote className="h-4 w-4" /> Reference & history
                </span>
                {showReference ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </button>
            )}
            {(isProspecting || showReference) && (<>
            {/* 7. Also gives to other Republicans (Active tab only) */}
            {(d.committee_giving && d.committee_giving.length > 0) && (
              <div className="bg-white border-2 border-flag-red/40 rounded-xl p-4 shadow-sm" data-testid="caller-detail-committees">
                <div className="text-xs font-bold uppercase tracking-wider text-flag-red mb-2">Also gives to other Republicans</div>
                <ul className="space-y-2 text-base sm:text-lg">
                  {d.committee_giving.map((c) => (
                    <li key={c.label} className="flex items-center justify-between border-b border-slate-100 pb-1.5 last:border-0" data-testid={`committee-${(c.label || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}>
                      <span className="text-slate-700">{c.label}</span>
                      <strong className="text-flag-navy tabular-nums">{fmtMoney(c.amount)}</strong>
                    </li>
                  ))}
                </ul>
                <div className="mt-2 pt-2 border-t border-slate-200 flex items-center justify-between text-sm">
                  <span className="text-slate-500 font-semibold uppercase tracking-wider text-xs">Total to other Republicans</span>
                  <strong className="text-flag-navy tabular-nums">{fmtMoney(d.committee_giving_total || 0)}</strong>
                </div>
              </div>
            )}

            {/* 8. Employer / occupation */}
            {(d.employer || d.occupation) && (
              <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
                <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Work</div>
                <div className="text-base text-slate-800 mt-0.5">{d.employer || "—"}</div>
                {d.occupation && <div className="text-sm text-slate-600">{d.occupation}</div>}
              </div>
            )}

            {/* 9. Giving — fully data-driven. Every row and every section
                header renders ONLY if the value is non-zero for THIS donor
                AND (for prior-cycle blocks) some donor in the campaign has
                any data for that cycle. Nothing collapses to "$0" or a
                dangling "—" label. Also fully skipped for prospecting
                categories (no giving to display). */}
            {!isProspecting && (() => {
              const history = d.giving_history || {};
              // Group prior-cycle history by year (2026 stays as its own
              // top block because it's the current cycle). Sorted desc.
              const priorYears = {};
              for (const [key, amount] of Object.entries(history)) {
                const m = key.match(/^(primary|general|cycle_to_date)_((?:19|20)\d{2})$/);
                if (!m) continue;
                const year = m[2];
                if (year === "2026") continue;  // shown in the current-cycle rows
                if (!priorYears[year]) priorYears[year] = {};
                priorYears[year][m[1]] = amount;
              }
              const yearOrder = Object.keys(priorYears).sort((a, b) => b.localeCompare(a));
              const currentCycleRows = [];
              if (d.last_gift_amount > 0 || d.last_gift_date) {
                currentCycleRows.push(
                  <GivingRow
                    key="last"
                    label="Last gift"
                    testid="giving-last-gift"
                    value={`${fmtMoney(d.last_gift_amount)}${d.last_gift_date ? ` on ${fmtDate(d.last_gift_date)}` : ""}`}
                  />
                );
              }
              if (d.highest_gift > 0) {
                currentCycleRows.push(
                  <GivingRow
                    key="highest"
                    label="Highest gift"
                    testid="giving-highest-gift"
                    value={`${fmtMoney(d.highest_gift)}${d.highest_gift_date ? ` on ${fmtDate(d.highest_gift_date)}` : ""}`}
                  />
                );
              }
              if (d.primary_2026 > 0) {
                currentCycleRows.push(<GivingRow key="p26" label="2026 primary" testid="giving-primary" value={fmtMoney(d.primary_2026)} />);
              }
              if (d.general_2026 > 0) {
                currentCycleRows.push(<GivingRow key="g26" label="2026 general" testid="giving-general" value={fmtMoney(d.general_2026)} />);
              }
              if (d.total_given > 0) {
                currentCycleRows.push(<GivingRow key="total" label="Total ever given" testid="giving-total" value={fmtMoney(d.total_given)} />);
              }
              // Nothing to show at all for this donor? Hide the entire card,
              // header included. Per Feb 2026 spec: no empty-labeled boxes.
              const hasAnyGiving = currentCycleRows.length > 0 || yearOrder.length > 0;
              if (!hasAnyGiving) return null;

              const fullHistoryRows = [];
              if (d.first_gift_date) {
                fullHistoryRows.push(<GivingRow key="first" label="First gift" value={fmtDate(d.first_gift_date)} muted />);
              }
              if (d.number_of_gifts > 0) {
                fullHistoryRows.push(<GivingRow key="ngifts" label="Number of gifts" value={String(d.number_of_gifts)} muted />);
              }
              if (d.avg_gift > 0) {
                fullHistoryRows.push(<GivingRow key="avg" label="Average gift" value={fmtMoney(d.avg_gift)} muted />);
              }
              if (d.cycle_to_date > 0) {
                fullHistoryRows.push(<GivingRow key="ctd" label="Cycle-to-date" value={fmtMoney(d.cycle_to_date)} muted />);
              }

              return (
                <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid="caller-detail-giving">
                  <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Giving</div>
                  {currentCycleRows.length > 0 && (
                    <dl className="space-y-3 text-lg">{currentCycleRows}</dl>
                  )}

                  {/* Prior-cycle blocks. Each year's header only renders if
                      the donor has any nonzero value for that year — and
                      because the importer stores only nonzero entries, the
                      whole block is naturally skipped when the candidate
                      didn't run in that year. */}
                  {yearOrder.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-slate-100 space-y-4" data-testid="giving-prior-cycles">
                      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Prior cycles</div>
                      {yearOrder.map((year) => {
                        const y = priorYears[year];
                        const parts = [];
                        if (y.primary > 0) parts.push({ key: "p", label: `${year} primary`, val: y.primary });
                        if (y.general > 0) parts.push({ key: "g", label: `${year} general`, val: y.general });
                        if (y.cycle_to_date > 0) parts.push({ key: "c", label: `${year} cycle-to-date`, val: y.cycle_to_date });
                        if (parts.length === 0) return null;
                        return (
                          <div key={year} data-testid={`giving-year-${year}`}>
                            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-600 mb-1.5">{year} Cycle</div>
                            <dl className="space-y-2 text-base">
                              {parts.map((p) => (
                                <GivingRow key={p.key} label={p.label} value={fmtMoney(p.val)} muted />
                              ))}
                            </dl>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {fullHistoryRows.length > 0 && (
                    <>
                      <button
                        type="button"
                        onClick={() => setShowFullHistory((v) => !v)}
                        className="mt-4 text-base font-semibold text-flag-navy underline underline-offset-2 hover:text-flag-red"
                        data-testid="giving-toggle-history"
                        aria-expanded={showFullHistory}
                      >
                        {showFullHistory ? "Hide full history" : "See full history"}
                      </button>
                      {showFullHistory && (
                        <dl className="mt-3 pt-3 border-t border-slate-100 space-y-2 text-base" data-testid="giving-full-history">
                          {fullHistoryRows}
                        </dl>
                      )}
                    </>
                  )}
                </div>
              );
            })()}
            </>)}
          </>
        )}
      </main>
      {/* Mobile-only fixed bottom bar — the caller can Call and open the
          Log outcome bottom sheet without scrolling. Skipped for the
          desktop inline layout (which is a side-pane view). */}
      {!isInline && d && (
        <div className="fixed bottom-0 left-0 right-0 z-20 bg-white border-t-2 border-slate-200 shadow-[0_-4px_12px_rgba(0,0,0,0.08)]" data-testid="caller-detail-sticky-bar">
          <div className="max-w-md sm:max-w-lg mx-auto px-3 py-2.5 flex items-center gap-2">
            {d.phone ? (
              <a
                href={telHref}
                onClick={() => recordChannel("call")}
                className="flex-[3] inline-flex items-center justify-center gap-2 h-14 rounded-xl bg-flag-red hover:bg-flag-red-hover text-white text-base font-black shadow-md"
                data-testid="caller-sticky-call"
              >
                <Phone className="h-5 w-5" /> Call
              </a>
            ) : (
              <button
                type="button"
                disabled
                className="flex-[3] inline-flex items-center justify-center gap-2 h-14 rounded-xl bg-slate-100 text-slate-400 text-base font-black cursor-not-allowed"
                data-testid="caller-sticky-call"
              >
                <Phone className="h-5 w-5" /> No phone
              </button>
            )}
            <button
              type="button"
              onClick={() => setShowOutcomeSheet(true)}
              className="flex-[2] inline-flex items-center justify-center gap-1.5 h-14 rounded-xl bg-flag-navy hover:bg-flag-navy/90 text-white text-sm font-bold shadow-md"
              data-testid="caller-sticky-log-outcome"
            >
              <CheckCircle2 className="h-5 w-5" /> Log outcome
            </button>
          </div>
        </div>
      )}
      {/* Mobile bottom sheet with the 5 outcome buttons — surfaces from
          the sticky bar so logging is one tap away. */}
      {!isInline && showOutcomeSheet && (
        <div
          className="fixed inset-0 z-40 bg-slate-900/50"
          onClick={() => setShowOutcomeSheet(false)}
          data-testid="outcome-sheet-backdrop"
        >
          <div
            className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl shadow-2xl p-4 pb-6"
            onClick={(e) => e.stopPropagation()}
            data-testid="outcome-sheet"
            role="dialog"
            aria-label="Log outcome"
          >
            <div className="flex items-center justify-between mb-1">
              <div className="text-sm font-bold uppercase tracking-wider text-flag-navy">Log outcome</div>
              <button
                type="button"
                onClick={() => setShowOutcomeSheet(false)}
                className="p-2 -m-2 text-slate-500 hover:text-flag-navy"
                data-testid="outcome-sheet-close"
                aria-label="Close outcome sheet"
              >
                <CloseIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="text-xs text-slate-500 mb-3">Advances to the next donor after logging.</div>
            <OutcomeGrid onPick={pickOutcome} />
          </div>
        </div>
      )}
      {/* Pledge amount capture — one field, pre-filled with the current
          ask. Skip logs the outcome without an amount, preserving the
          one-tap happy path when the caller didn't get a firm number. */}
      {pledgePrompt && d && (
        <PledgeSheet
          defaultAmount={
            smActive
              ? smSelectedAmount
              : askMode === "highest" && d.highest_gift > 0
                ? Math.round(d.highest_gift)
                : Math.round(d.general_room || 0)
          }
          onSkip={() => setStatus("pledged")}
          onSave={(amt) => setStatus("pledged", { pledge_amount: amt })}
          onCancel={() => setPledgePrompt(false)}
        />
      )}
      {/* Follow-up date picker — three big word-labeled options, matches
          the 65+ mobile constraint. Skip logs the follow-up without a
          date so the caller isn't forced through a picker they don't
          want. */}
      {followUpPrompt && d && (
        <FollowUpSheet
          onSkip={() => setStatus("call_back")}
          onSave={(dateIso) => setStatus("call_back", { follow_up_date: dateIso })}
          onCancel={() => setFollowUpPrompt(false)}
        />
      )}
    </div>
  );
}

function PrimaryCallButton({ href, disabled, onClick }) {
  if (disabled) {
    return (
      <button
        type="button"
        disabled
        className="w-full inline-flex items-center justify-center gap-2 h-16 rounded-xl bg-slate-100 text-slate-400 text-lg font-black cursor-not-allowed"
        data-testid="caller-action-call"
      >
        <Phone className="h-6 w-6" /> No phone on file
      </button>
    );
  }
  return (
    <a
      href={href}
      onClick={onClick}
      className="w-full inline-flex items-center justify-center gap-2 h-16 rounded-xl bg-flag-red hover:bg-flag-red-hover text-white text-lg font-black shadow-md"
      data-testid="caller-action-call"
    >
      <Phone className="h-6 w-6" /> Call
    </a>
  );
}

function ActionButton({ href, disabled, icon: Icon, label, testid, onClick, secondary = false }) {
  const baseCls = "inline-flex flex-col items-center justify-center gap-1 min-h-[52px] rounded-xl text-sm font-bold transition-colors";
  if (disabled) {
    return (
      <button
        type="button"
        disabled
        className={`${baseCls} bg-slate-100 text-slate-400 cursor-not-allowed`}
        data-testid={testid}
      >
        <Icon className="h-5 w-5" /> {label}
      </button>
    );
  }
  const activeCls = secondary
    ? "bg-white border-2 border-flag-navy text-flag-navy hover:bg-flag-navy hover:text-white shadow-sm"
    : "bg-flag-red hover:bg-flag-red-hover text-white shadow-md";
  return (
    <a href={href} onClick={onClick} className={`${baseCls} ${activeCls}`} data-testid={testid}>
      <Icon className="h-5 w-5" /> {label}
    </a>
  );
}

function GivingRow({ label, value, muted, testid }) {
  return (
    <div className="flex items-baseline justify-between gap-3" data-testid={testid}>
      <dt className={`${muted ? "text-slate-500" : "text-slate-600"} font-medium`}>{label}</dt>
      <dd className={`text-right font-bold ${muted ? "text-slate-700" : "text-flag-navy"} tabular-nums`}>{value}</dd>
    </div>
  );
}

/**
 * Consistent color-coded outcome grid used both inline in the detail page
 * and inside the mobile bottom sheet. 56px tall targets + bold labels so a
 * 65+ user on a phone can hit them without misfiring.
 */
function OutcomeGrid({ onPick }) {
  return (
    <div className="grid grid-cols-2 gap-2.5">
      <OutcomeButton onClick={() => onPick("reached")} icon={CheckCircle2} label="Reached" testid="caller-outcome-reached" tone="reached" />
      <OutcomeButton onClick={() => onPick("left_message")} icon={Voicemail} label="Left message" testid="caller-outcome-left-message" tone="left_message" />
      <OutcomeButton onClick={() => onPick("pledged")} icon={DollarSign} label="Pledged" testid="caller-outcome-pledged" tone="pledged" />
      <OutcomeButton onClick={() => onPick("declined")} icon={XCircle} label="Declined" testid="caller-outcome-declined" tone="declined" />
      <OutcomeButton onClick={() => onPick("call_back")} icon={Clock} label="Follow up" testid="caller-outcome-callback" tone="call_back" className="col-span-2" />
    </div>
  );
}

const OUTCOME_TONE = {
  reached:      "bg-white border-emerald-500 text-emerald-900 hover:bg-emerald-50",
  left_message: "bg-white border-amber-500 text-amber-900 hover:bg-amber-50",
  pledged:      "bg-emerald-600 border-emerald-600 text-white hover:bg-emerald-700",
  declined:     "bg-white border-slate-400 text-slate-700 hover:bg-slate-100",
  call_back:    "bg-white border-sky-500 text-sky-900 hover:bg-sky-50",
};

function OutcomeButton({ onClick, icon: Icon, label, testid, tone, className = "" }) {
  const toneCls = OUTCOME_TONE[tone] || "bg-white border-slate-200 text-slate-700 hover:border-flag-navy hover:text-flag-navy";
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center justify-center gap-2 h-14 rounded-lg border-2 text-base font-bold transition-colors ${toneCls} ${className}`}
    >
      <Icon className="h-5 w-5" /> {label}
    </button>
  );
}

/* --------------------------------------------------------------------- */
/**
 * Reusable bottom-sheet frame — the 5-outcome sheet, the pledge prompt,
 * and the follow-up picker all share the same slide-up-from-bottom card
 * shape so the 65+ mobile user gets a consistent motion vocabulary.
 */
function BottomSheet({ title, subtitle, onDismiss, testid, children }) {
  return (
    <div
      className="fixed inset-0 z-40 bg-slate-900/50"
      onClick={onDismiss}
      data-testid={`${testid}-backdrop`}
    >
      <div
        className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl shadow-2xl p-4 pb-6"
        onClick={(e) => e.stopPropagation()}
        data-testid={testid}
        role="dialog"
        aria-label={title}
      >
        <div className="flex items-center justify-between mb-1">
          <div className="text-sm font-bold uppercase tracking-wider text-flag-navy">{title}</div>
          <button
            type="button"
            onClick={onDismiss}
            className="p-2 -m-2 text-slate-500 hover:text-flag-navy"
            data-testid={`${testid}-close`}
            aria-label="Close"
          >
            <CloseIcon className="h-5 w-5" />
          </button>
        </div>
        {subtitle && <div className="text-xs text-slate-500 mb-3">{subtitle}</div>}
        {children}
      </div>
    </div>
  );
}

/**
 * Single-field pledge capture. Pre-filled with the current ask so the
 * happy path is Confirm → advance. Skip logs Pledged without an amount
 * so the caller isn't blocked when the donor pledged "some amount TBD".
 */
function PledgeSheet({ defaultAmount, onSave, onSkip, onCancel }) {
  const [val, setVal] = useState(String(defaultAmount || ""));
  const inputRef = useRef(null);
  useEffect(() => {
    // Focus + select so a single tap replaces the pre-filled figure.
    const t = setTimeout(() => {
      try { inputRef.current?.focus(); inputRef.current?.select(); } catch { /* noop */ }
    }, 40);
    return () => clearTimeout(t);
  }, []);
  const parsed = Number(String(val).replace(/[^\d.]/g, ""));
  const valid = Number.isFinite(parsed) && parsed > 0;
  const submit = () => {
    if (!valid) return;
    onSave(Math.round(parsed));
  };
  return (
    <BottomSheet
      title="How much pledged?"
      subtitle="Pre-filled with the current ask. Confirm the number, edit, or skip."
      onDismiss={onCancel}
      testid="pledge-sheet"
    >
      <div className="relative">
        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-2xl font-bold text-slate-500">$</span>
        <Input
          ref={inputRef}
          type="text"
          inputMode="numeric"
          value={val}
          onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
          className="h-16 pl-10 text-3xl font-bold text-flag-navy tabular-nums"
          data-testid="pledge-amount-input"
        />
      </div>
      <div className="mt-4 grid grid-cols-1 gap-2">
        <Button
          type="button"
          onClick={submit}
          disabled={!valid}
          className="h-14 bg-emerald-600 hover:bg-emerald-700 text-white text-base font-black shadow-md disabled:opacity-50"
          data-testid="pledge-save"
        >
          Save pledge
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={onSkip}
          className="h-12 border-2 text-base font-bold text-slate-600"
          data-testid="pledge-skip"
        >
          Skip (log Pledged without amount)
        </Button>
      </div>
    </BottomSheet>
  );
}

function _isoDate(offsetDays = 0) {
  const now = new Date();
  now.setDate(now.getDate() + offsetDays);
  return now.toISOString().slice(0, 10);
}

/**
 * Follow-up scheduling — three big word-labeled options + a "Pick a
 * date" affordance that reveals a native date input. No dense calendar
 * widget; the target user is 65+ on a phone.
 */
function FollowUpSheet({ onSave, onSkip, onCancel }) {
  const [customOpen, setCustomOpen] = useState(false);
  const [customDate, setCustomDate] = useState(_isoDate(0));
  return (
    <BottomSheet
      title="When to follow up?"
      subtitle="Choose one — this shows the donor under Follow-ups due on that date."
      onDismiss={onCancel}
      testid="followup-sheet"
    >
      {!customOpen ? (
        <div className="grid grid-cols-1 gap-2.5">
          <button
            type="button"
            onClick={() => onSave(_isoDate(1))}
            className="h-14 rounded-lg border-2 border-sky-500 bg-white text-sky-900 hover:bg-sky-50 text-base font-bold"
            data-testid="followup-tomorrow"
          >
            Tomorrow
          </button>
          <button
            type="button"
            onClick={() => onSave(_isoDate(7))}
            className="h-14 rounded-lg border-2 border-sky-500 bg-white text-sky-900 hover:bg-sky-50 text-base font-bold"
            data-testid="followup-next-week"
          >
            Next week
          </button>
          <button
            type="button"
            onClick={() => setCustomOpen(true)}
            className="h-14 rounded-lg border-2 border-slate-300 bg-white text-flag-navy hover:border-flag-navy text-base font-bold"
            data-testid="followup-pick-date"
          >
            Pick a date
          </button>
          <button
            type="button"
            onClick={onSkip}
            className="h-12 text-sm font-bold text-slate-500 hover:text-flag-navy"
            data-testid="followup-skip"
          >
            Skip (log Follow up without a date)
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          <label className="text-sm font-semibold text-slate-700" htmlFor="followup-date-input">Follow-up date</label>
          <input
            id="followup-date-input"
            type="date"
            value={customDate}
            min={_isoDate(0)}
            onChange={(e) => setCustomDate(e.target.value)}
            className="w-full h-14 px-3 rounded-lg border-2 border-slate-300 text-lg font-bold text-flag-navy focus:border-flag-navy focus:outline-none"
            data-testid="followup-date-input"
          />
          <div className="grid grid-cols-2 gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setCustomOpen(false)}
              className="h-12 border-2 text-sm font-bold"
              data-testid="followup-date-back"
            >
              Back
            </Button>
            <Button
              type="button"
              onClick={() => customDate && onSave(customDate)}
              disabled={!customDate}
              className="h-12 bg-flag-navy hover:bg-flag-navy/90 text-white text-sm font-black"
              data-testid="followup-date-save"
            >
              Save follow-up
            </Button>
          </div>
        </div>
      )}
    </BottomSheet>
  );
}


const OUTCOME_FILTER_OPTIONS = [
  { value: "all", label: "All outcomes" },
  { value: "reached", label: "Reached" },
  { value: "left_message", label: "Left message" },
  { value: "pledged", label: "Pledged" },
  { value: "declined", label: "Declined" },
  { value: "call_back", label: "Follow up" },
];
const CHANNEL_LABEL = { call: "Call", text: "Text", email: "Email" };

function fmtLogDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return iso;
  }
}

/* --------------------------------------------------------------------- */
/**
 * CallerSettings — mounted at /caller/settings. Three sections:
 *   1. Donor Message editor (shared with the owner portal — same template).
 *   2. Outreach Log (this candidate's own campaign only, filterable, CSV export).
 *   3. Change password.
 */
export function CallerSettings() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // Per-list messages ----------------------------------------------
  const [categories, setCategories] = useState(null);
  const [openCategoryId, setOpenCategoryId] = useState(null);

  // Outreach Log -------------------------------------------------
  const [logOutcome, setLogOutcome] = useState("all");
  const [logData, setLogData] = useState(null);
  const [logLoading, setLogLoading] = useState(false);

  // Password -----------------------------------------------------
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showCur, setShowCur] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [busy, setBusy] = useState(false);

  // Load own categories so the per-list message editor accordion can render.
  const loadCategories = useCallback(async () => {
    try {
      const { data } = await api.get("/caller/categories");
      setCategories(data.categories || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  }, []);
  useEffect(() => { loadCategories(); }, [loadCategories]);

  // Load outreach log whenever the filter changes.
  useEffect(() => {
    let cancelled = false;
    setLogLoading(true);
    const url = logOutcome === "all" ? "/caller/log" : `/caller/log?outcome=${logOutcome}`;
    api.get(url)
      .then(({ data }) => { if (!cancelled) setLogData(data); })
      .catch((err) => toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message))
      .finally(() => { if (!cancelled) setLogLoading(false); });
    return () => { cancelled = true; };
  }, [logOutcome]);

  const exportLog = async () => {
    try {
      const url = logOutcome === "all" ? "/caller/log/export" : `/caller/log/export?outcome=${logOutcome}`;
      const resp = await api.get(url, { responseType: "blob" });
      const blob = new Blob([resp.data], { type: "text/csv" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      const today = new Date().toISOString().slice(0, 10);
      link.download = `outreach-log-${today}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(link.href);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const validatePw = () => {
    if (!cur) return "Enter your current password.";
    if (next.length < 8) return "New password must be at least 8 characters.";
    if (next === cur) return "New password must be different from your current password.";
    if (next !== confirm) return "New password and confirmation don't match.";
    return null;
  };

  const submitPw = async (e) => {
    e.preventDefault();
    const err = validatePw();
    if (err) { toast.error(err); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: cur,
        new_password: next,
      });
      toast.success("Password updated");
      setCur(""); setNext(""); setConfirm("");
      navigate("/caller");
    } catch (err2) {
      toast.error(formatApiErrorDetail(err2.response?.data?.detail) || err2.message);
    } finally {
      setBusy(false);
    }
  };

  const entries = logData?.entries || [];
  const counts = logData?.counts || {};
  const totalLogged = Object.values(counts).reduce((a, b) => a + b, 0);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="caller-settings-root">
      <header className="sticky top-0 z-20 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link
            to="/caller"
            className="inline-flex items-center gap-1 text-sm font-semibold text-slate-600 hover:text-flag-navy"
            data-testid="caller-settings-back"
          >
            <ArrowLeft className="h-4 w-4" /> Back to call list
          </Link>
          <button
            type="button"
            onClick={logout}
            className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-flag-red"
            data-testid="caller-settings-logout"
          >
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-flag-red">Settings</div>
          <h1 className="font-display text-2xl font-black text-flag-navy">Your campaign</h1>
          <p className="text-xs text-slate-500 mt-1">Signed in as {user?.email}.</p>
        </div>

        {/* ============================================================ */}
        {/* Per-list messages — Text + Email per category               */}
        {/* ============================================================ */}
        <section className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 space-y-3" data-testid="caller-settings-message-card">
          <div>
            <h2 className="font-display text-lg font-black text-flag-navy inline-flex items-center gap-1.5">
              <MessageCircle className="h-4 w-4" /> Donor messages
            </h2>
            <p className="text-[11px] text-slate-500 leading-snug mt-1">
              Each list has its own Text and Email templates. Click a list to open its editors. The owner portal edits the same templates — whichever save is most recent applies.
            </p>
          </div>
          {categories === null ? (
            <p className="text-sm text-slate-500 italic py-3">Loading your lists…</p>
          ) : categories.length === 0 ? (
            <p className="text-sm text-slate-500 italic py-3">You don&rsquo;t have any donor lists yet. Once your admin uploads a list, you can edit its messages here.</p>
          ) : (
            <ul className="divide-y divide-slate-100 border border-slate-200 rounded-lg" data-testid="caller-settings-list-accordion">
              {categories.map((c) => {
                const isOpen = openCategoryId === c.id;
                const catType = c.category_type || "call_list";
                const isProspecting = catType === "prospecting_list";
                const isPac = catType === "pac_list";
                const isMaxed = catType === "maxed";
                const typeBadge =
                  isProspecting ? { cls: "bg-amber-100 text-amber-900", label: "Prospecting" }
                  : isPac ? { cls: "bg-emerald-100 text-emerald-900", label: "PAC List" }
                  : isMaxed ? { cls: "bg-flag-red/10 text-flag-red", label: "Maxed" }
                  : { cls: "bg-slate-100 text-slate-700", label: "Call List" };
                const setType = async (nextType) => {
                  if (nextType === catType) return;
                  try {
                    await api.patch(`/caller/categories/${c.id}`, { category_type: nextType });
                    await loadCategories();
                    toast.success(`"${c.name}" is now a ${
                      nextType === "pac_list" ? "PAC List"
                      : nextType === "prospecting_list" ? "Prospecting List"
                      : "Call List"
                    }`);
                  } catch (err) {
                    toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
                  }
                };
                return (
                  <li key={c.id} data-testid={`caller-settings-list-${c.id}`}>
                    <button
                      type="button"
                      onClick={() => setOpenCategoryId(isOpen ? null : c.id)}
                      className="w-full flex items-center justify-between gap-2 px-3 py-3 text-left hover:bg-slate-50"
                      aria-expanded={isOpen}
                      data-testid={`caller-settings-list-toggle-${c.id}`}
                    >
                      <div className="min-w-0">
                        <div className="font-semibold text-flag-navy text-sm inline-flex items-center gap-2">
                          {c.name}
                          <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${typeBadge.cls}`}>
                            {typeBadge.label}
                          </span>
                        </div>
                        <div className="text-[11px] text-slate-500 mt-0.5">{c.count.toLocaleString()} donor{c.count === 1 ? "" : "s"}</div>
                      </div>
                      <span className="text-[11px] font-bold uppercase tracking-wider text-flag-navy">
                        {isOpen ? "Close" : "Edit"}
                      </span>
                    </button>
                    {isOpen && (
                      <div className="px-3 pb-4 border-t border-slate-100 bg-slate-50" data-testid={`caller-settings-list-editor-${c.id}`}>
                        {!isMaxed && (
                          <div className="pt-3 flex items-center gap-2 flex-wrap">
                            <span className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold">Type:</span>
                            <div className="inline-flex rounded-md overflow-hidden border border-slate-300" role="radiogroup" aria-label="Category type">
                              <button
                                type="button" role="radio" aria-checked={catType === "call_list"}
                                onClick={() => setType("call_list")}
                                className={`px-2.5 py-1 text-[11px] font-bold ${catType === "call_list" ? "bg-flag-navy text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}
                                data-testid={`caller-settings-type-call-${c.id}`}
                              >Call List</button>
                              <button
                                type="button" role="radio" aria-checked={catType === "pac_list"}
                                onClick={() => setType("pac_list")}
                                className={`px-2.5 py-1 text-[11px] font-bold border-l border-slate-300 ${catType === "pac_list" ? "bg-emerald-600 text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}
                                data-testid={`caller-settings-type-pac-${c.id}`}
                              >PAC List</button>
                              <button
                                type="button" role="radio" aria-checked={catType === "prospecting_list"}
                                onClick={() => setType("prospecting_list")}
                                className={`px-2.5 py-1 text-[11px] font-bold border-l border-slate-300 ${catType === "prospecting_list" ? "bg-amber-500 text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}
                                data-testid={`caller-settings-type-prospecting-${c.id}`}
                              >Prospecting List</button>
                            </div>
                            <span className="text-[10.5px] text-slate-500">Changing type just re-renders the cards — no donor data is touched.</span>
                          </div>
                        )}
                        <div className="pt-3">
                          <MessageEditor
                            scope="candidate"
                            categoryId={c.id}
                            categoryType={c.category_type || "call_list"}
                            categoryName={c.name}
                            testIdPrefix={`caller-msg-${c.id}`}
                          />
                        </div>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {/* ============================================================ */}
        {/* Outreach Log — this candidate's own campaign only            */}
        {/* ============================================================ */}
        <section className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 space-y-3" data-testid="caller-settings-log-card">
          <div className="flex items-baseline justify-between flex-wrap gap-2">
            <div>
              <h2 className="font-display text-lg font-black text-flag-navy inline-flex items-center gap-1.5">
                <StickyNote className="h-4 w-4" /> Outreach Log
              </h2>
              <p className="text-[11px] text-slate-500 mt-0.5" data-testid="caller-settings-log-total">
                {totalLogged.toLocaleString()} entr{totalLogged === 1 ? "y" : "ies"} across all outcomes
              </p>
            </div>
            <button
              type="button"
              onClick={exportLog}
              disabled={entries.length === 0}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border-2 border-flag-navy text-flag-navy text-sm font-bold hover:bg-flag-navy hover:text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              data-testid="caller-settings-log-export"
            >
              <Download className="h-4 w-4" /> Export CSV
            </button>
          </div>

          <div className="flex flex-wrap gap-1.5" data-testid="caller-settings-log-filters">
            {OUTCOME_FILTER_OPTIONS.map((o) => {
              const active = logOutcome === o.value;
              const c = o.value === "all" ? totalLogged : (counts[o.value] || 0);
              return (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => setLogOutcome(o.value)}
                  data-testid={`caller-settings-log-filter-${o.value}`}
                  className={`text-[11px] font-bold uppercase tracking-wider px-2.5 py-1.5 rounded-full border transition-colors ${
                    active
                      ? "bg-flag-red text-white border-flag-red"
                      : "bg-slate-50 text-slate-700 border-slate-200 hover:border-flag-red"
                  }`}
                >
                  {o.label} <span className="opacity-80 ml-1">({c})</span>
                </button>
              );
            })}
          </div>

          {logLoading ? (
            <p className="text-sm text-slate-500 py-4">Loading…</p>
          ) : entries.length === 0 ? (
            <p className="text-sm text-slate-500 py-4" data-testid="caller-settings-log-empty">
              No entries yet. Log an outcome on a donor and it will appear here — most recent first.
            </p>
          ) : (
            <ul className="divide-y divide-slate-100 -mx-2" data-testid="caller-settings-log-list">
              {entries.map((e) => (
                <li key={e.id} className="px-2 py-3" data-testid={`caller-settings-log-entry-${e.id}`}>
                  <div className="flex items-baseline justify-between gap-2 flex-wrap">
                    <div>
                      <span className="font-semibold text-flag-navy">{e.donor_name}</span>
                      <span className={`ml-2 inline-flex items-center text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${STATUS_LABELS[e.outcome]?.cls || "bg-slate-100 text-slate-700"}`}>
                        {STATUS_LABELS[e.outcome]?.label || e.outcome}
                      </span>
                    </div>
                    <span className="text-[11px] text-slate-500 tabular-nums">{fmtLogDate(e.created_at)}</span>
                  </div>
                  <div className="mt-1 flex items-baseline gap-3 text-[11px] text-slate-600 flex-wrap">
                    <span>
                      <span className="text-slate-400">Channel initiated:</span>{" "}
                      {e.channel_initiated ? <strong className="text-flag-navy">{CHANNEL_LABEL[e.channel_initiated]}</strong> : <span className="italic text-slate-400">none</span>}
                    </span>
                    {e.pledge_amount != null && (
                      <span data-testid={`log-pledge-${e.id}`}>
                        <span className="text-slate-400">Pledged:</span>{" "}
                        <strong className="text-emerald-700">${Math.round(Number(e.pledge_amount)).toLocaleString()}</strong>
                      </span>
                    )}
                    {e.follow_up_date && (
                      <span data-testid={`log-followup-${e.id}`}>
                        <span className="text-slate-400">Follow up:</span>{" "}
                        <strong className="text-sky-700">{e.follow_up_date}</strong>
                      </span>
                    )}
                  </div>
                  {e.note && (
                    <p className="mt-1.5 text-sm text-slate-700 leading-snug whitespace-pre-wrap">{e.note}</p>
                  )}
                </li>
              ))}
            </ul>
          )}

          <p className="text-[10.5px] text-slate-500 leading-snug border-t border-slate-100 pt-3">
            <strong>Channel initiated</strong> records which outreach button (Call / Text / Email) was tapped in the app — <em>not</em> a delivery confirmation. Actual sending happens in your phone&rsquo;s native app.
          </p>
        </section>

        {/* ============================================================ */}
        {/* Change password                                              */}
        {/* ============================================================ */}
        <section className="bg-white border border-slate-200 rounded-xl shadow-sm p-5" data-testid="caller-settings-password-card">
          <h2 className="font-display text-lg font-black text-flag-navy inline-flex items-center gap-1.5">
            <KeyRound className="h-4 w-4" /> Change password
          </h2>
          <form onSubmit={submitPw} className="mt-3 space-y-4">
            <PasswordField
              id="cur-pw"
              label="Current password"
              value={cur}
              onChange={setCur}
              show={showCur}
              toggleShow={() => setShowCur((v) => !v)}
              autoComplete="current-password"
              testid="caller-settings-current"
            />
            <PasswordField
              id="new-pw"
              label="New password"
              value={next}
              onChange={setNext}
              show={showNext}
              toggleShow={() => setShowNext((v) => !v)}
              autoComplete="new-password"
              helper="At least 8 characters. Must differ from your current password."
              testid="caller-settings-new"
            />
            <PasswordField
              id="confirm-pw"
              label="Confirm new password"
              value={confirm}
              onChange={setConfirm}
              show={showNext}
              toggleShow={() => setShowNext((v) => !v)}
              autoComplete="new-password"
              testid="caller-settings-confirm"
            />
            <Button
              type="submit"
              disabled={busy}
              className="w-full h-11 bg-flag-red hover:bg-flag-red-hover text-white font-bold"
              data-testid="caller-settings-submit"
            >
              <KeyRound className="h-4 w-4 mr-1.5" /> {busy ? "Updating..." : "Update password"}
            </Button>
          </form>
        </section>
      </main>
    </div>
  );
}

function PasswordField({ id, label, value, onChange, show, toggleShow, autoComplete, helper, testid }) {
  return (
    <div>
      <Label htmlFor={id} className="text-xs font-bold uppercase tracking-wider text-slate-600">{label}</Label>
      <div className="mt-1 relative">
        <Input
          id={id}
          type={show ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete={autoComplete}
          spellCheck={false}
          className="h-11 pr-10"
          data-testid={testid}
        />
        <button
          type="button"
          onClick={toggleShow}
          className="absolute inset-y-0 right-2 my-auto text-slate-400 hover:text-flag-navy"
          aria-label={show ? "Hide password" : "Show password"}
          tabIndex={-1}
        >
          {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
      {helper && <p className="mt-1 text-[11px] text-slate-500 leading-snug">{helper}</p>}
    </div>
  );
}
